import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO, { PageSEO } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Programs = () => {
  const { t, language } = useTranslation();
  const [activeTab, setActiveTab] = useState('weekday');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [selectedType, setSelectedType] = useState('all'); // New program type filter
  const [weeklyView, setWeeklyView] = useState(false); // New weekly Monday-Friday view
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [liveBroadcastSchedule, setLiveBroadcastSchedule] = useState(null);
  
  // Day Schedule filter - defaults to current day in Africa/Monrovia timezone
  const getCurrentDayInMonrovia = () => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const now = new Date();
    // Africa/Monrovia is GMT+0
    const monroviaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Africa/Monrovia' }));
    return days[monroviaTime.getDay()];
  };
  const [selectedScheduleDay, setSelectedScheduleDay] = useState(getCurrentDayInMonrovia);
  
  // Preaching Schedule Filter
  const [preachingCountryFilter, setPreachingCountryFilter] = useState('all');
  const [preachingDayFilter, setPreachingDayFilter] = useState('all');
  
  // Live Broadcast Days filters
  const [selectedDay, setSelectedDay] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedBroadcastType, setSelectedBroadcastType] = useState('all');
  
  const [loading, setLoading] = useState(true);

  // Helper function to translate program names
  const translateProgramName = (programName) => {
    // Map of program names to translation keys
    const programMap = {
      "Music & Reflection": "musicReflection",
      "Morning Music & Reflection": "morningMusicReflection",
      "Community Announcements": "communityAnnouncements",
      "Community Programming": "communityProgramming",
      "Morning Devotional": "morningDevotional",
      "Morning Devotional & Prayer": "morningDevotionalPrayer",
      "Evening Devotional": "eveningDevotional",
      "Morning Prayer & Worship": "morningPrayerWorship",
      "Daily Sermon": "dailySermon",
      "Evening News & Roundup": "eveningNews",
      "Evening Programming": "eveningProgramming",
      "Christian Teaching": "christianTeaching",
      "Phone-in Program": "phoneInProgram",
      "Youth Connect": "youthConnect",
      "Youth Connect Special": "youthConnectSpecial",
      "Women's Empowerment Hour": "womensEmpowerment",
      "Gbandi Language Hour": "gbandiLanguageHour",
      "Gbandi Language Hour (continued)": "gbandiLanguageHourContinued",
      "Mandingo Bible Teaching": "mandingoTeaching",
      "Mandingo Programming": "mandingoProgramming",
      "Mandingo Programming (continued)": "mandingoProgrammingContinued",
      "French Programming Transition": "frenchProgrammingTransition",
      "Pre-Service Worship": "preServiceWorship",
      "Post-Service Reflection": "postServiceReflection",
      "Live Partner Church Service": "livePartnerChurch",
      "Sunday Evening Worship": "sundayEveningWorship",
      "Thru the Bible (TTB)": "thruTheBible",
      "VNA French Satellite Feed": "vnaFrenchSatelliteFeed",
      "Guidelines for Living": "guidelinesForLiving",
      "Reset by Bonnie Sala": "resetBySala",
      "Makona Talk Show": "makonaTalkShow"
    };

    // Direct match first
    if (programMap[programName]) {
      return t(`programs.programNames.${programMap[programName]}`);
    }
    
    // Handle "Pastor's Corner - Country" variations
    if (programName.startsWith("Pastor's Corner")) {
      const suffix = programName.replace("Pastor's Corner", "").trim();
      return t('programs.programNames.pastorsCorner') + (suffix ? " " + suffix : "");
    }
    
    // Keep branded program names in original language
    return programName;
  };

  // === PHASE 1: BACKUP & PREVIEW INFRASTRUCTURE ===
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [changeLog, setChangeLog] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // Admin access check (could be enhanced with proper auth later)
  const [adminPassword, setAdminPassword] = useState('');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const ADMIN_PASSWORD = 'kioo-admin-2025'; // Simple password for now

  // === BACKUP & PREVIEW FUNCTIONS ===
  
  // Load backup history from localStorage on component mount
  useEffect(() => {
    const savedBackups = localStorage.getItem('kioo-programs-backups');
    const savedChangeLog = localStorage.getItem('kioo-programs-changelog');
    
    if (savedBackups) {
      setBackupHistory(JSON.parse(savedBackups));
    }
    if (savedChangeLog) {
      setChangeLog(JSON.parse(savedChangeLog));
    }
  }, []);

  // Create a backup snapshot of current schedule data
  const createBackupSnapshot = () => {
    const timestamp = new Date().toISOString();
    const backup = {
      id: `backup-${Date.now()}`,
      timestamp,
      data: {
        weekdaySchedule: [...weekdaySchedule],
        saturdaySchedule: [...saturdaySchedule],
        sundaySchedule: [...sundaySchedule],
        weeklySpecial: [...weeklySpecial],
        liveBroadcastSchedule: liveBroadcastSchedule ? { ...liveBroadcastSchedule } : null
      },
      description: `Backup created on ${new Date(timestamp).toLocaleString()}`,
      version: `v${backupHistory.length + 1}`
    };

    const newBackupHistory = [backup, ...backupHistory].slice(0, 10); // Keep last 10 backups
    setBackupHistory(newBackupHistory);
    localStorage.setItem('kioo-programs-backups', JSON.stringify(newBackupHistory));
    
    // Log the backup creation
    addToChangeLog(`Backup snapshot created: ${backup.version}`, 'backup');
    
    return backup.id;
  };

  // Restore from a backup
  const restoreFromBackup = (backupId) => {
    const backup = backupHistory.find(b => b.id === backupId);
    if (!backup) return false;

    // In a real implementation, this would restore the actual schedule data
    // For now, we'll just log the action
    addToChangeLog(`Schedule restored from backup ${backup.version} (${backup.timestamp})`, 'restore');
    
    // Toggle out of preview mode after restore
    setIsPreviewMode(false);
    return true;
  };

  // Add entry to change log
  const addToChangeLog = (description, type = 'change', details = null) => {
    const entry = {
      id: `change-${Date.now()}`,
      timestamp: new Date().toISOString(),
      description,
      type, // 'change', 'backup', 'restore', 'preview'
      details,
      user: 'admin' // In real implementation, this would be the authenticated user
    };

    const newChangeLog = [entry, ...changeLog].slice(0, 50); // Keep last 50 entries
    setChangeLog(newChangeLog);
    localStorage.setItem('kioo-programs-changelog', JSON.stringify(newChangeLog));
  };

  // Toggle preview mode
  const togglePreviewMode = () => {
    if (!isPreviewMode) {
      // Entering preview mode - create backup first
      createBackupSnapshot();
      addToChangeLog('Preview mode activated', 'preview');
    } else {
      // Exiting preview mode
      addToChangeLog('Preview mode deactivated', 'preview');
    }
    setIsPreviewMode(!isPreviewMode);
  };

  // Admin authentication
  const handleAdminLogin = () => {
    if (adminPassword === ADMIN_PASSWORD) {
      setIsAdminAuthenticated(true);
      setShowAdminPanel(true);
      addToChangeLog('Admin panel accessed', 'admin');
    } else {
      alert('Invalid admin password');
    }
    setAdminPassword('');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [programsRes, scheduleRes, liveScheduleRes] = await Promise.all([
          axios.get(`${API}/programs`),
          axios.get(`${API}/programs/schedule`),
          axios.get(`${API}/live-broadcast-schedule`)
        ]);
        
        setPrograms(programsRes.data);
        setSchedule(scheduleRes.data);
        setLiveBroadcastSchedule(liveScheduleRes.data);
      } catch (error) {
        console.error('Error fetching programs:', error);
        // Fall back to static data if API is not available
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Complete schedule data - Updated with Phase 2 PASTORAL ENHANCEMENTS
  const weekdaySchedule = [
    { "Start Time": "00:00", "End Time": "00:30", "Program": "Music & Reflection", "Language": "English", "Type": "Music" },
    { "Start Time": "00:30", "End Time": "01:00", "Program": "Community Announcements", "Language": "English", "Type": "Live Services" },
    { "Start Time": "01:00", "End Time": "01:30", "Program": "Phone-in Program (Live)", "Language": "English", "Type": "Interactive" },
    { "Start Time": "01:30", "End Time": "02:00", "Program": "Music & Reflection", "Language": "English", "Type": "Music" },
    { "Start Time": "02:00", "End Time": "02:30", "Program": "Community Programming (Live)", "Language": "Kissi", "Type": "Community" },
    { "Start Time": "02:30", "End Time": "03:00", "Program": "Music & Reflection", "Language": "Kissi", "Type": "Music" },
    { "Start Time": "03:00", "End Time": "03:30", "Program": "Community Announcements", "Language": "Kissi", "Type": "Live Services" },
    { "Start Time": "03:30", "End Time": "04:00", "Program": "Phone-in Program (Live)", "Language": "Kissi", "Type": "Interactive" },
    { "Start Time": "04:00", "End Time": "04:25", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "04:25", "End Time": "04:26", "Program": "Focus Famille", "Language": "French", "Type": "Famille", "highlight": true, "slug": "focus-famille-daily-0425-fr", "recurrence": "daily", "effective_date": "2026-01-01" },
    { "Start Time": "04:26", "End Time": "04:55", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "04:55", "End Time": "04:56", "Program": "Share Life Africa", "Language": "French", "Type": "Dévotion", "highlight": true, "slug": "share-life-africa-daily-0455-fr", "recurrence": "daily", "effective_date": "2026-01-01" },
    { "Start Time": "04:56", "End Time": "05:00", "Program": "Music & Reflection", "Language": "French", "Type": "Music" },
    { "Start Time": "05:00", "End Time": "05:15", "Program": "Morning Devotional", "Language": "English", "Type": "Devotional" },
    { "Start Time": "05:15", "End Time": "05:45", "Program": "Morning Prayer & Worship", "Language": "English", "Type": "Worship" },
    { "Start Time": "05:45", "End Time": "06:00", "Program": "Community Announcements", "Language": "English", "Type": "Live Services" },
    { "Start Time": "06:00", "End Time": "06:30", "Program": "Thru the Bible (TTB)", "Language": "English", "Type": "Bible Teaching" },
    { "Start Time": "06:30", "End Time": "07:00", "Program": "Morning Devotional Preaching", "Language": "English", "Type": "Sermon", "highlight": true },
    { "Start Time": "07:00", "End Time": "07:30", "Program": "Love & Faith", "Language": "English", "Type": "Bible Teaching", "highlight": true },
    { "Start Time": "07:30", "End Time": "08:00", "Program": "Music & Reflection", "Language": "English", "Type": "Music" },
    { "Start Time": "08:00", "End Time": "08:30", "Program": "Thru the Bible (TTB)", "Language": "Kissi", "Type": "Bible Teaching" },
    { "Start Time": "08:30", "End Time": "09:00", "Program": "Community Programming (Live)", "Language": "Kissi", "Type": "Community" },
    { "Start Time": "09:00", "End Time": "09:55", "Program": "VNA French Satellite Feed", "Language": "French", "Type": "Satellite" },
    { "Start Time": "09:55", "End Time": "09:56", "Program": "La Bible Dit", "Language": "French", "Type": "Dévotion", "highlight": true, "slug": "la-bible-dit-daily-0955-fr", "recurrence": "daily", "effective_date": "2026-01-01" },
    { "Start Time": "09:56", "End Time": "10:00", "Program": "French Programming Transition", "Language": "French", "Type": "Transition", "friday_override": { "Program": "Mission Afrique Pour Christ", "Language": "French", "Type": "Missions", "slug": "mission-afrique-christ-friday-0956-fr", "notes": "15min with presenter integration" } },
    { "Start Time": "10:00", "End Time": "10:30", "Program": "Pastor's Corner - Liberia", "Language": "English", "Type": "Sermon", "highlight": true },
    { "Start Time": "10:30", "End Time": "11:00", "Program": "Spot Light English", "Language": "English", "Type": "Educational", "highlight": true },
    { "Start Time": "11:00", "End Time": "11:05", "Program": "Guidelines for Living", "Language": "English", "Type": "Bible Teaching", "highlight": true, "weekdayOnly": true },
    { "Start Time": "11:05", "End Time": "11:30", "Program": "Community Announcements", "Language": "English", "Type": "Live Services" },
    { "Start Time": "11:30", "End Time": "11:32", "Program": "Reset by Bonnie Sala", "Language": "English", "Type": "Bible Teaching", "highlight": true, "weekdayOnly": true },
    { "Start Time": "11:32", "End Time": "12:00", "Program": "Music & Reflection", "Language": "English", "Type": "Music" },
    { "Start Time": "12:00", "End Time": "12:30", "Program": "Community Programming (Live)", "Language": "Kissi", "Type": "Community", "thursday_override": { "Program": "ARM Foya", "Language": "English", "Type": "Live Services", "highlight": true, "notes": "ARM Foya special program on Thursday" } },
    { "Start Time": "12:30", "End Time": "13:00", "Program": "Youth Connect", "Language": "Kissi", "Type": "Youth/Community" },
    { "Start Time": "13:00", "End Time": "13:30", "Program": "Community Announcements", "Language": "Kissi", "Type": "Live Services" },
    { "Start Time": "13:30", "End Time": "14:00", "Program": "Phone-in Program (Live)", "Language": "Kissi", "Type": "Interactive" },
    { "Start Time": "14:00", "End Time": "14:30", "Program": "Thru the Bible (TTB)", "Language": "French", "Type": "Bible Teaching" },
    { "Start Time": "14:30", "End Time": "15:00", "Program": "Pastor's Corner - Sierra Leone", "Language": "English", "Type": "Sermon", "highlight": true },
    { "Start Time": "15:00", "End Time": "15:30", "Program": "Music & Reflection", "Language": "Mixed", "Type": "Music" },
    { "Start Time": "15:30", "End Time": "15:35", "Program": "Guidelines for Living", "Language": "French", "Type": "Bible Teaching", "highlight": true, "weekdayOnly": true },
    { "Start Time": "15:35", "End Time": "16:00", "Program": "Music & Reflection", "Language": "French", "Type": "Music" },
    { "Start Time": "16:00", "End Time": "16:30", "Program": "Christian Teaching", "Language": "Mandingo", "Type": "Bible Teaching", "highlight": true, "friday_override": { "Program": "Renaissance", "Language": "French", "Type": "Interactive", "highlight": true, "notes": "Weekly French interactive program - first half" } },
    { "Start Time": "16:30", "End Time": "17:00", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community", "monday_override": { "Program": "Mandingo Programming", "Language": "Mandingo", "Type": "Live Services", "highlight": true, "notes": "Mandingo community programming - first half" }, "tuesday_override": { "Program": "Mandingo Bible Teaching", "Language": "Mandingo", "Type": "Bible Teaching", "highlight": true, "notes": "30min Mandingo Bible teaching" }, "friday_override": { "Program": "Renaissance (continued)", "Language": "French", "Type": "Interactive", "highlight": true, "notes": "Weekly French interactive program - second half" } },
    // === PREACHING SCHEDULE - 3 NATION UNITY (with varied breaks) ===
    // 17:00-17:45 Council of Churches (Liberia) - Mon/Tue/Wed, Thursday: Shadrach Lendor, Friday: Global Free Pentecostal
    { "Start Time": "17:00", "End Time": "17:45", "Program": "Council of Churches", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Liberia", "monday_override": { "Program": "Council of Churches", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Liberia" }, "tuesday_override": { "Program": "Council of Churches", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Liberia" }, "wednesday_override": { "Program": "Council of Churches", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Liberia" }, "thursday_override": { "Program": "Shadrach Lendor", "Language": "English", "Type": "Bible Teaching", "highlight": true, "country": "Liberia", "notes": "Weekly Bible teaching" }, "friday_override": { "Program": "Global Free Pentecostal Church", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Liberia", "notes": "Weekly Friday preaching" } },
    // Break: Live Presenter + Short Programs (varied by day)
    { "Start Time": "17:45", "End Time": "18:00", "Program": "Live Presenter & Announcements", "Language": "Mixed", "Type": "Interactive", "monday_override": { "Program": "Live Presenter & Announcements", "Language": "Mixed", "Type": "Interactive" }, "tuesday_override": { "Program": "Community Spotlight", "Language": "English", "Type": "Live Services" }, "wednesday_override": { "Program": "Live Presenter & Music", "Language": "Mixed", "Type": "Interactive" }, "thursday_override": { "Program": "Music & Reflection", "Language": "Mixed", "Type": "Music" }, "friday_override": { "Program": "Live Presenter & Announcements", "Language": "Mixed", "Type": "Interactive" } },
    // 18:00-18:45 Sierra Leone & Guinea Pastors (rotating by day)
    { "Start Time": "18:00", "End Time": "18:45", "Program": "International Preaching Hour", "Language": "English", "Type": "Sermon", "highlight": true, "monday_override": { "Program": "Rev Philip Tamba Kamara", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Sierra Leone", "notes": "Sierra Leone Pastor" }, "tuesday_override": { "Program": "Rev Pasteur Esaïe Saa Koundouno", "Language": "French", "Type": "Sermon", "highlight": true, "country": "Guinea", "notes": "Guinea Pastor" }, "wednesday_override": { "Program": "Rev Joseph Abu Lavally", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Sierra Leone", "notes": "Sierra Leone Pastor" }, "thursday_override": { "Program": "Rev Pasteur Timothe Millimouno", "Language": "French", "Type": "Sermon", "highlight": true, "country": "Guinea", "notes": "Guinea Pastor" }, "friday_override": { "Program": "Rev Victor Tamba Bundor", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Sierra Leone", "notes": "Sierra Leone Pastor" } },
    // Break: Music + Live Presenter (30 min varied)
    { "Start Time": "18:45", "End Time": "19:00", "Program": "Music & Reflection", "Language": "Mixed", "Type": "Music" },
    { "Start Time": "19:00", "End Time": "19:15", "Program": "Live Presenter & Requests", "Language": "Mixed", "Type": "Live Services", "monday_override": { "Program": "Gbandi Language Hour", "Language": "Gbandi", "Type": "Live Services", "highlight": true, "notes": "Weekly Gbandi community programming" }, "tuesday_override": { "Program": "Live Presenter & Requests", "Language": "Mixed", "Type": "Live Services" }, "wednesday_override": { "Program": "Music & Reflection", "Language": "Mixed", "Type": "Music" }, "thursday_override": { "Program": "Live Presenter & Requests", "Language": "Mixed", "Type": "Live Services" }, "friday_override": { "Program": "Women's Empowerment Hour", "Language": "Mixed", "Type": "Live Services", "highlight": true, "notes": "Weekly women's empowerment" } },
    // 19:15-20:00 Guinea & Sierra Leone Pastors (second rotation) / Monday: Gbandi / Friday: Women's Empowerment
    { "Start Time": "19:15", "End Time": "20:00", "Program": "Evening Preaching", "Language": "Mixed", "Type": "Sermon", "highlight": true, "monday_override": { "Program": "Gbandi Language Hour (continued)", "Language": "Gbandi", "Type": "Live Services", "highlight": true, "notes": "Gbandi programming continues" }, "tuesday_override": { "Program": "Rev Joseph F Bobor", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Sierra Leone", "notes": "Sierra Leone Pastor" }, "wednesday_override": { "Program": "Rev Pasteur Laurent Fara Kamano", "Language": "French", "Type": "Sermon", "highlight": true, "country": "Guinea", "notes": "Guinea Pastor" }, "thursday_override": { "Program": "Rev Joseph Tengbeh", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Sierra Leone", "notes": "Sierra Leone Pastor" }, "friday_override": { "Program": "Women's Empowerment Hour (continued)", "Language": "Mixed", "Type": "Live Services", "highlight": true, "notes": "Women's empowerment continues" } },
    // Break: Music + Live Presenter before evening programs
    { "Start Time": "20:00", "End Time": "20:15", "Program": "Music & Reflection", "Language": "Mixed", "Type": "Music" },
    { "Start Time": "20:15", "End Time": "20:30", "Program": "Live Presenter & Evening Update", "Language": "Mixed", "Type": "Live Services", "thursday_override": { "Program": "Free Pentecostal Church (Bomah)", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Liberia", "notes": "Weekly Thursday preaching - first part" } },
    // 20:30-21:00 Kissi Heritage (Mon/Fri) / Free Pentecostal continued (Thu) / ARM Foya (Wed)
    { "Start Time": "20:30", "End Time": "21:00", "Program": "Phone-in Program (Live)", "Language": "Kissi", "Type": "Interactive", "monday_override": { "Program": "Kissi Heritage Foundation", "Language": "Kissi", "Type": "Educational", "highlight": true, "country": "Liberia", "notes": "30-minute heritage broadcast" }, "wednesday_override": { "Program": "ARM Foya", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Liberia", "notes": "Weekly Wednesday preaching" }, "thursday_override": { "Program": "Free Pentecostal Church (Bomah)", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Liberia", "notes": "Weekly Thursday preaching - second part" }, "friday_override": { "Program": "Kissi Heritage Foundation", "Language": "Kissi", "Type": "Educational", "highlight": true, "country": "Liberia", "notes": "30-minute heritage broadcast" } },
    // Regular programming resumes
    { "Start Time": "21:00", "End Time": "21:30", "Program": "Thru the Bible (TTB)", "Language": "English", "Type": "Bible Teaching" },
    { "Start Time": "21:30", "End Time": "22:00", "Program": "Music & Reflection", "Language": "Mixed", "Type": "Music" },
    { "Start Time": "22:00", "End Time": "22:02", "Program": "Reset by Bonnie Sala", "Language": "French", "Type": "Bible Teaching", "highlight": true, "weekdayOnly": true },
    { "Start Time": "22:02", "End Time": "22:30", "Program": "Thru the Bible (TTB)", "Language": "French", "Type": "Bible Teaching" },
    { "Start Time": "22:30", "End Time": "23:00", "Program": "Community Announcements", "Language": "Mixed", "Type": "Live Services" },
    { "Start Time": "23:00", "End Time": "23:30", "Program": "Music & Reflection", "Language": "Mixed", "Type": "Music" },
    { "Start Time": "23:30", "End Time": "00:00", "Program": "Evening Devotional", "Language": "Mixed", "Type": "Devotional" }
  ];

  const saturdaySchedule = [
    { "Start Time": "00:00", "End Time": "01:00", "Program": "Music & Reflection", "Language": "English", "Type": "Music" },
    { "Start Time": "01:00", "End Time": "02:00", "Program": "Community Programming (Live)", "Language": "English", "Type": "Community" },
    { "Start Time": "02:00", "End Time": "03:00", "Program": "Community Programming (Live)", "Language": "Kissi", "Type": "Community" },
    { "Start Time": "03:00", "End Time": "03:25", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "03:25", "End Time": "03:26", "Program": "Focus Famille", "Language": "French", "Type": "Famille", "highlight": true, "slug": "focus-famille-saturday-0325-fr", "recurrence": "daily", "effective_date": "2026-01-01" },
    { "Start Time": "03:26", "End Time": "03:55", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "03:55", "End Time": "03:56", "Program": "Share Life Africa", "Language": "French", "Type": "Dévotion", "highlight": true, "slug": "share-life-africa-saturday-0355-fr", "recurrence": "daily", "effective_date": "2026-01-01" },
    { "Start Time": "03:56", "End Time": "04:00", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "04:00", "End Time": "05:00", "Program": "Music & Reflection", "Language": "French", "Type": "Music" },
    { "Start Time": "05:00", "End Time": "06:00", "Program": "Morning Devotional & Prayer", "Language": "English", "Type": "Devotional" },
    { "Start Time": "06:00", "End Time": "09:00", "Program": "Makona Talk Show", "Language": "English, French & Kissi", "Type": "Interactive", "highlight": true },
    { "Start Time": "09:00", "End Time": "09:30", "Program": "Truth for Life", "Language": "English", "Type": "Bible Teaching", "highlight": true },
    { "Start Time": "09:30", "End Time": "10:00", "Program": "Daily Sermon", "Language": "English", "Type": "Sermon", "highlight": true },
    { "Start Time": "10:00", "End Time": "11:00", "Program": "Evangelist Billy Bimba - English Hour", "Language": "English", "Type": "Sermon", "highlight": true },
    { "Start Time": "11:00", "End Time": "13:00", "Program": "NLASN 'Island Praise'", "Language": "English", "Type": "Satellite", "highlight": true },
    { "Start Time": "13:00", "End Time": "14:00", "Program": "Community Programming (Live)", "Language": "English", "Type": "Community" },
    { "Start Time": "14:00", "End Time": "14:40", "Program": "Découverte de la Bible – Série Jeunesse", "Language": "French", "Type": "Jeunesse", "highlight": true, "slug": "decouverte-bible-jeunesse-saturday-1400-fr", "recurrence": "weekly", "effective_date": "2026-01-01", "notes": "40min youth program moved to weekend for better accessibility" },
    { "Start Time": "14:40", "End Time": "15:00", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "15:00", "End Time": "16:00", "Program": "Community Programming (Live)", "Language": "English", "Type": "Community" },
    { "Start Time": "16:00", "End Time": "17:00", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "17:00", "End Time": "18:00", "Program": "Community Programming (Live)", "Language": "Kissi", "Type": "Community" },
    // Saturday Preaching: 18:00-18:45 Pasteur Joseph Saa Kamano (Guinea)
    { "Start Time": "18:00", "End Time": "18:45", "Program": "Pasteur Joseph Saa Kamano", "Language": "French", "Type": "Sermon", "highlight": true, "country": "Guinea", "notes": "Guinea Pastor - Weekly Saturday preaching" },
    { "Start Time": "18:45", "End Time": "19:00", "Program": "Music & Reflection", "Language": "Mixed", "Type": "Music" },
    // Saturday Preaching: 19:00-19:45 Rev Pasteur Daniel Sekou Kamano (Guinea) - moved from Friday
    { "Start Time": "19:00", "End Time": "19:45", "Program": "Rev Pasteur Daniel Sekou Kamano", "Language": "French", "Type": "Sermon", "highlight": true, "country": "Guinea", "notes": "Guinea Pastor - Weekly Saturday preaching" },
    { "Start Time": "19:45", "End Time": "20:00", "Program": "Music & Reflection", "Language": "Mixed", "Type": "Music" },
    // Saturday Preaching: 20:00-20:45 Light Streams Chapel (Liberia)
    { "Start Time": "20:00", "End Time": "20:45", "Program": "Light Streams Chapel", "Language": "English", "Type": "Sermon", "highlight": true, "country": "Liberia", "notes": "Weekly Saturday preaching" },
    { "Start Time": "20:45", "End Time": "21:00", "Program": "Music & Reflection", "Language": "English", "Type": "Music" },
    { "Start Time": "21:00", "End Time": "22:00", "Program": "Community Programming (Live)", "Language": "Kissi", "Type": "Community" },
    { "Start Time": "22:00", "End Time": "23:00", "Program": "Community Programming (Live)", "Language": "Mixed", "Type": "Community" },
    { "Start Time": "23:00", "End Time": "00:00", "Program": "Music & Reflection", "Language": "Mixed", "Type": "Music" }
  ];

  const sundaySchedule = [
    { "Start Time": "00:00", "End Time": "01:00", "Program": "Music & Reflection", "Language": "English", "Type": "Music" },
    { "Start Time": "01:00", "End Time": "02:00", "Program": "Music & Reflection", "Language": "English", "Type": "Music" },
    { "Start Time": "02:00", "End Time": "03:00", "Program": "Community Programming (Live)", "Language": "Kissi", "Type": "Community" },
    { "Start Time": "03:00", "End Time": "03:25", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "03:25", "End Time": "03:26", "Program": "Focus Famille", "Language": "French", "Type": "Famille", "highlight": true, "slug": "focus-famille-sunday-0325-fr", "recurrence": "daily", "effective_date": "2026-01-01" },
    { "Start Time": "03:26", "End Time": "03:55", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "03:55", "End Time": "03:56", "Program": "Share Life Africa", "Language": "French", "Type": "Dévotion", "highlight": true, "slug": "share-life-africa-sunday-0355-fr", "recurrence": "daily", "effective_date": "2026-01-01" },
    { "Start Time": "03:56", "End Time": "04:00", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "04:00", "End Time": "05:00", "Program": "Music & Reflection", "Language": "French", "Type": "Music" },
    { "Start Time": "05:00", "End Time": "06:00", "Program": "Morning Devotional & Prayer", "Language": "English", "Type": "Devotional" },
    { "Start Time": "06:00", "End Time": "07:00", "Program": "Evangelist Billy Bimba - Kissi Hour", "Language": "Kissi", "Type": "Sermon", "highlight": true },
    { "Start Time": "07:00", "End Time": "07:30", "Program": "Morning Music & Reflection", "Language": "French", "Type": "Music" },
    { "Start Time": "07:30", "End Time": "08:00", "Program": "Daily Sermon", "Language": "English", "Type": "Sermon", "highlight": true },
    { "Start Time": "08:00", "End Time": "09:00", "Program": "Community Programming (Live)", "Language": "Kissi", "Type": "Community" },
    { "Start Time": "09:00", "End Time": "10:00", "Program": "Pre-Service Worship", "Language": "Mixed", "Type": "Worship" },
    { "Start Time": "10:00", "End Time": "12:00", "Program": "Live Partner Church Service", "Language": "English/French/Mixed", "Type": "Live Service", "highlight": true },
    { "Start Time": "12:00", "End Time": "13:00", "Program": "Post-Service Reflection", "Language": "Mixed", "Type": "Devotional" },
    { "Start Time": "13:00", "End Time": "14:00", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "14:00", "End Time": "15:00", "Program": "La Vie Chez Nous", "Language": "French", "Type": "Interactive", "highlight": true },
    { "Start Time": "15:00", "End Time": "16:00", "Program": "Community Programming (Live)", "Language": "English", "Type": "Community" },
    { "Start Time": "16:00", "End Time": "17:00", "Program": "Community Programming (Live)", "Language": "French", "Type": "Community" },
    { "Start Time": "17:00", "End Time": "18:00", "Program": "ARM Foya", "Language": "English", "Type": "Live Services", "highlight": true },
    { "Start Time": "18:00", "End Time": "19:00", "Program": "Community Programming (Live)", "Language": "Mixed", "Type": "Community" },
    { "Start Time": "19:00", "End Time": "20:00", "Program": "Sunday Evening Worship", "Language": "Mixed", "Type": "Worship" },
    { "Start Time": "20:00", "End Time": "21:00", "Program": "Sunday Gospel with Sam Ngardu", "Language": "English", "Type": "Gospel" },
    { "Start Time": "21:00", "End Time": "21:30", "Program": "Truth for Life", "Language": "French", "Type": "Bible Teaching", "highlight": true },
    { "Start Time": "21:30", "End Time": "22:00", "Program": "Music & Reflection", "Language": "English", "Type": "Music" },
    { "Start Time": "22:00", "End Time": "23:00", "Program": "Community Programming (Live)", "Language": "Mixed", "Type": "Community" },
    { "Start Time": "23:00", "End Time": "00:00", "Program": "Evening Devotional", "Language": "Mixed", "Type": "Devotional" }
  ];

  // Special weekly programs - Updated with French programs effective 2026-01-01
  const weeklySpecial = [
    // === 3-NATION PREACHING SCHEDULE (with varied breaks between sermons) ===
    // LIBERIA - Foya Pastors & Churches
    { "Day": "Monday", "Time": "17:00-17:45", "Program": "Council of Churches", "Language": "English", "Type": "Sermon", "badge": "🇱🇷 Liberia", "country": "Liberia" },
    { "Day": "Tuesday", "Time": "17:00-17:45", "Program": "Council of Churches", "Language": "English", "Type": "Sermon", "badge": "🇱🇷 Liberia", "country": "Liberia" },
    { "Day": "Wednesday", "Time": "17:00-17:45", "Program": "Council of Churches", "Language": "English", "Type": "Sermon", "badge": "🇱🇷 Liberia", "country": "Liberia" },
    { "Day": "Thursday", "Time": "17:00-17:45", "Program": "Shadrach Lendor", "Language": "English", "Type": "Bible Teaching", "badge": "🇱🇷 Liberia", "country": "Liberia", "notes": "Weekly Bible teaching" },
    { "Day": "Friday", "Time": "17:00-17:45", "Program": "Global Free Pentecostal Church", "Language": "English", "Type": "Sermon", "badge": "🇱🇷 Liberia", "country": "Liberia", "notes": "Weekly Friday preaching" },
    { "Day": "Monday", "Time": "20:30-21:00", "Program": "Kissi Heritage Foundation", "Language": "Kissi", "Type": "Educational", "badge": "🇱🇷 Liberia", "country": "Liberia", "notes": "30-minute heritage broadcast" },
    { "Day": "Friday", "Time": "20:30-21:00", "Program": "Kissi Heritage Foundation", "Language": "Kissi", "Type": "Educational", "badge": "🇱🇷 Liberia", "country": "Liberia", "notes": "30-minute heritage broadcast" },
    { "Day": "Wednesday", "Time": "20:30-21:00", "Program": "ARM Foya", "Language": "English", "Type": "Sermon", "badge": "🇱🇷 Liberia", "country": "Liberia", "notes": "Weekly Wednesday preaching" },
    { "Day": "Thursday", "Time": "20:15-21:00", "Program": "Free Pentecostal Church (Bomah)", "Language": "English", "Type": "Sermon", "badge": "🇱🇷 Liberia", "country": "Liberia", "notes": "Weekly Thursday preaching" },
    { "Day": "Saturday", "Time": "20:00-20:45", "Program": "Light Streams Chapel", "Language": "English", "Type": "Sermon", "badge": "🇱🇷 Liberia", "country": "Liberia" },
    // SIERRA LEONE Pastors (with varied breaks between sermons)
    { "Day": "Monday", "Time": "18:00-18:45", "Program": "Rev Philip Tamba Kamara", "Language": "English", "Type": "Sermon", "badge": "🇸🇱 Sierra Leone", "country": "Sierra Leone" },
    { "Day": "Tuesday", "Time": "19:15-20:00", "Program": "Rev Joseph F Bobor", "Language": "English", "Type": "Sermon", "badge": "🇸🇱 Sierra Leone", "country": "Sierra Leone" },
    { "Day": "Wednesday", "Time": "18:00-18:45", "Program": "Rev Joseph Abu Lavally", "Language": "English", "Type": "Sermon", "badge": "🇸🇱 Sierra Leone", "country": "Sierra Leone" },
    { "Day": "Thursday", "Time": "19:15-20:00", "Program": "Rev Joseph Tengbeh", "Language": "English", "Type": "Sermon", "badge": "🇸🇱 Sierra Leone", "country": "Sierra Leone" },
    { "Day": "Friday", "Time": "18:00-18:45", "Program": "Rev Victor Tamba Bundor", "Language": "English", "Type": "Sermon", "badge": "🇸🇱 Sierra Leone", "country": "Sierra Leone" },
    // GUINEA Pastors (with varied breaks between sermons)
    { "Day": "Tuesday", "Time": "18:00-18:45", "Program": "Rev Pasteur Esaïe Saa Koundouno", "Language": "French", "Type": "Sermon", "badge": "🇬🇳 Guinea", "country": "Guinea" },
    { "Day": "Wednesday", "Time": "19:15-20:00", "Program": "Rev Pasteur Laurent Fara Kamano", "Language": "French", "Type": "Sermon", "badge": "🇬🇳 Guinea", "country": "Guinea" },
    { "Day": "Thursday", "Time": "18:00-18:45", "Program": "Rev Pasteur Timothe Millimouno", "Language": "French", "Type": "Sermon", "badge": "🇬🇳 Guinea", "country": "Guinea" },
    { "Day": "Friday", "Time": "19:15-20:00", "Program": "Women's Empowerment Hour", "Language": "Mixed", "Type": "Live Services", "badge": "⭐ Weekly Special", "notes": "Women's empowerment continues" },
    { "Day": "Saturday", "Time": "19:00-19:45", "Program": "Rev Pasteur Daniel Sekou Kamano", "Language": "French", "Type": "Sermon", "badge": "🇬🇳 Guinea", "country": "Guinea", "notes": "Moved from Friday to Saturday" },
    { "Day": "Saturday", "Time": "18:00-18:45", "Program": "Pasteur Joseph Saa Kamano", "Language": "French", "Type": "Sermon", "badge": "🇬🇳 Guinea", "country": "Guinea" },
    // === OTHER WEEKLY SPECIALS ===
    { "Day": "Monday", "Time": "19:00-20:00", "Program": "Gbandi Language Hour", "Language": "Gbandi", "Type": "Live Services", "badge": "⭐ Weekly Special", "notes": "Weekly Gbandi community programming" },
    { "Day": "Friday", "Time": "19:00-20:00", "Program": "Women's Empowerment Hour", "Language": "Mixed", "Type": "Live Services", "badge": "⭐ Weekly Special", "notes": "Weekly women's empowerment program" },
    { "Day": "Monday", "Time": "16:30-17:00", "Program": "Mandingo Programming", "Language": "Mandingo", "Type": "Live Services", "badge": "⭐ Local Special", "notes": "Mandingo community programming" },
    { "Day": "Tuesday", "Time": "16:30-17:00", "Program": "Mandingo Bible Teaching", "Language": "Mandingo", "Type": "Bible Teaching", "badge": "⭐ Bible Special", "notes": "30min Mandingo Bible teaching" },
    { "Day": "Friday", "Time": "16:00-17:00", "Program": "Renaissance", "Language": "French", "Type": "Interactive", "badge": "⭐ French Special", "notes": "Weekly French interactive program with Isabelle Kenne" },
    { "Day": "Friday", "Time": "09:45-10:00", "Program": "Mission Afrique Pour Christ", "Language": "French", "Type": "Missions", "badge": "⭐ French Special", "slug": "mission-afrique-christ-friday-0945-fr", "recurrence": "weekly", "effective_date": "2026-01-01", "notes": "15min with presenter integration within VNA block" },
    { "Day": "Saturday", "Time": "14:00-14:40", "Program": "Découverte de la Bible – Série Jeunesse", "Language": "French", "Type": "Jeunesse", "badge": "⭐ French Youth Special", "slug": "decouverte-bible-jeunesse-saturday-1400-fr", "recurrence": "weekly", "effective_date": "2026-01-01", "notes": "40min youth program moved to weekend for better accessibility" },
    { "Day": "Saturday", "Time": "14:00-15:00", "Program": "Youth Connect Special", "Language": "English", "Type": "Youth/Community", "badge": "⭐ Weekly Special" },
    { "Day": "Saturday", "Time": "06:00-09:00", "Program": "Makona Talk Show", "Language": "English, French & Kissi", "Type": "Interactive", "badge": "⭐ Weekly Special", "description": "All 3 countries meeting to discuss issues faced by our people - Highly interactive" },
    { "Day": "Sunday", "Time": "10:00-12:00", "Program": "Live Partner Church Service", "Language": "English/French/Mixed", "Type": "Live Service", "badge": "⭐ Sunday Special", "notes": "Rotating 3-nation church broadcasts" }
  ];

  // Color mapping for program types
  const typeColors = {
    'Bible Teaching': 'bg-blue-100 text-blue-800',
    'Community': 'bg-green-100 text-green-800',
    'Interactive': 'bg-orange-100 text-orange-800',
    'Satellite': 'bg-purple-100 text-purple-800',
    'Live Service': 'bg-red-100 text-red-800',
    'Music': 'bg-yellow-100 text-yellow-800',
    'Devotional': 'bg-indigo-100 text-indigo-800',
    'Sermon': 'bg-pink-100 text-pink-800',
    'Youth/Community': 'bg-cyan-100 text-cyan-800',
    'Educational': 'bg-teal-100 text-teal-800',
    'Worship': 'bg-rose-100 text-rose-800',
    'Gospel': 'bg-amber-100 text-amber-800',
    // French program types effective 2026-01-01
    'Magazine': 'bg-violet-100 text-violet-800',
    'Jeunesse': 'bg-lime-100 text-lime-800',
    'Missions': 'bg-emerald-100 text-emerald-800',
    'Dévotion': 'bg-sky-100 text-sky-800',
    'Famille': 'bg-fuchsia-100 text-fuchsia-800',
    'Transition': 'bg-gray-100 text-gray-800'
  };

  // Language options - Added Gbandi
  const languages = ['all', 'English', 'French', 'Kissi', 'Fula', 'Mandingo', 'Gbandi', 'Mixed'];
  
  // Program type options for filtering
  const programTypes = [
    'all',
    'Bible Teaching',
    'Community', 
    'Interactive',
    'Satellite',
    'Live Service',
    'Music',
    'Devotional',
    'Worship',
    'Sermon',
    'Youth/Community',
    'Outreach',
    'Educational'
  ];
  
  // Live Broadcast Days filter options
  const days = ['all', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const countries = ['all', 'liberia', 'sierra_leone', 'guinea'];
  const broadcastTypes = ['all', 'live', 'pre-recorded', 'rotation'];

  // Filter Live Broadcast Schedule
  const getFilteredBroadcastSchedule = () => {
    if (!liveBroadcastSchedule) return null;
    
    let filteredSchedule = { ...liveBroadcastSchedule.weeklySchedule };
    
    // Filter by selected day
    if (selectedDay !== 'all') {
      filteredSchedule = { [selectedDay]: filteredSchedule[selectedDay] };
    }
    
    return {
      ...liveBroadcastSchedule,
      weeklySchedule: filteredSchedule
    };
  };

  // Get filtered country schedules
  const getFilteredCountrySchedules = () => {
    if (!liveBroadcastSchedule) return [];
    
    let filtered = liveBroadcastSchedule.countrySchedules;
    
    // Filter by selected country
    if (selectedCountry !== 'all') {
      const countryName = selectedCountry === 'sierra_leone' ? 'Sierra Leone' : 
                         selectedCountry === 'liberia' ? 'Liberia' : 'Guinea';
      filtered = filtered.filter(country => country.country === countryName);
    }
    
    return filtered;
  };

  // Check if a day/country combination matches the broadcast type filter
  const matchesBroadcastTypeFilter = (status) => {
    if (selectedBroadcastType === 'all') return true;
    return status === selectedBroadcastType;
  };

  // Sync activeTab with selectedScheduleDay
  useEffect(() => {
    if (selectedScheduleDay === 'saturday') {
      setActiveTab('saturday');
    } else if (selectedScheduleDay === 'sunday') {
      setActiveTab('sunday');
    } else {
      setActiveTab('weekday');
    }
  }, [selectedScheduleDay]);

  useEffect(() => {
    let baseSchedule = [];
    if (activeTab === 'weekday') baseSchedule = weekdaySchedule;
    else if (activeTab === 'saturday') baseSchedule = saturdaySchedule;
    else if (activeTab === 'sunday') baseSchedule = sundaySchedule;

    // Apply day-specific overrides for weekday schedules
    let schedule = baseSchedule.map(program => {
      // Check for day-specific overrides (monday_override, tuesday_override, etc.)
      const overrideKey = `${selectedScheduleDay}_override`;
      if (program[overrideKey]) {
        return {
          ...program,
          Program: program[overrideKey].Program,
          Language: program[overrideKey].Language || program.Language,
          Type: program[overrideKey].Type || program.Type,
          highlight: program[overrideKey].highlight || program.highlight,
          notes: program[overrideKey].notes || program.notes
        };
      }
      return program;
    });

    // Apply both language and type filters
    let filtered = schedule;
    
    // Filter by language
    if (selectedLanguage !== 'all') {
      filtered = filtered.filter(program => 
        program.Language.toLowerCase().includes(selectedLanguage.toLowerCase())
      );
    }
    
    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(program => 
        program.Type === selectedType
      );
    }
    
    setFilteredPrograms(filtered);
  }, [activeTab, selectedLanguage, selectedType, selectedScheduleDay]);

  const getCurrentSchedule = () => {
    if (activeTab === 'weekday') return weekdaySchedule;
    if (activeTab === 'saturday') return saturdaySchedule;
    if (activeTab === 'sunday') return sundaySchedule;
    return [];
  };

  const getScheduleTitle = () => {
    // Capitalize the selected day name
    const dayName = selectedScheduleDay.charAt(0).toUpperCase() + selectedScheduleDay.slice(1);
    return `${dayName} Schedule`;
  };

  // Generate pie chart data for visual representation
  const generatePieChartData = (schedule) => {
    const typeCount = {};
    schedule.forEach(program => {
      const type = program.Type;
      typeCount[type] = (typeCount[type] || 0) + 1;
    });
    return typeCount;
  };

  // Preaching Schedule Data
  const preachingScheduleData = {
    liberia: [
      { pastor: "Council of Churches", days: "Mon, Tue, Wed", time: "17:00-17:45", type: "Sermon" },
      { pastor: "Shadrach Lendor", days: "Thursday", time: "17:00-17:45", type: "Bible Teaching" },
      { pastor: "Global Free Pentecostal Church", days: "Friday", time: "17:00-17:45", type: "Sermon" },
      { pastor: "ARM Foya", days: "Wednesday", time: "20:30-21:00", type: "Sermon" },
      { pastor: "Free Pentecostal Church (Bomah)", days: "Thursday", time: "20:15-21:00", type: "Sermon" },
      { pastor: "Light Streams Chapel", days: "Saturday", time: "20:00-20:45", type: "Sermon" },
      { pastor: "Kissi Heritage Foundation", days: "Mon, Fri", time: "20:30-21:00", type: "Educational" }
    ],
    sierraLeone: [
      { pastor: "Rev Philip Tamba Kamara", days: "Monday", time: "18:00-18:45", type: "Sermon" },
      { pastor: "Rev Joseph F Bobor", days: "Tuesday", time: "19:15-20:00", type: "Sermon" },
      { pastor: "Rev Joseph Abu Lavally", days: "Wednesday", time: "18:00-18:45", type: "Sermon" },
      { pastor: "Rev Joseph Tengbeh", days: "Thursday", time: "19:15-20:00", type: "Sermon" },
      { pastor: "Rev Victor Tamba Bundor", days: "Friday", time: "18:00-18:45", type: "Sermon" }
    ],
    guinea: [
      { pastor: "Rev Pasteur Esaïe Saa Koundouno", days: "Tuesday", time: "18:00-18:45", type: "Sermon" },
      { pastor: "Rev Pasteur Laurent Fara Kamano", days: "Wednesday", time: "19:15-20:00", type: "Sermon" },
      { pastor: "Rev Pasteur Timothe Millimouno", days: "Thursday", time: "18:00-18:45", type: "Sermon" },
      { pastor: "Pasteur Joseph Saa Kamano", days: "Saturday", time: "18:00-18:45", type: "Sermon" },
      { pastor: "Rev Pasteur Daniel Sekou Kamano", days: "Saturday", time: "19:00-19:45", type: "Sermon" }
    ]
  };

  // Filter preaching schedule based on selected filters
  const getFilteredPreachingSchedule = () => {
    let result = { liberia: [], sierraLeone: [], guinea: [] };
    
    const filterByDay = (items) => {
      if (preachingDayFilter === 'all') return items;
      return items.filter(item => {
        const days = item.days.toLowerCase();
        const filterDay = preachingDayFilter.toLowerCase();
        if (filterDay === 'monday') return days.includes('mon');
        if (filterDay === 'tuesday') return days.includes('tue');
        if (filterDay === 'wednesday') return days.includes('wed');
        if (filterDay === 'thursday') return days.includes('thu');
        if (filterDay === 'friday') return days.includes('fri');
        if (filterDay === 'saturday') return days.includes('sat');
        if (filterDay === 'sunday') return days.includes('sun');
        return true;
      });
    };

    if (preachingCountryFilter === 'all' || preachingCountryFilter === 'liberia') {
      result.liberia = filterByDay(preachingScheduleData.liberia);
    }
    if (preachingCountryFilter === 'all' || preachingCountryFilter === 'sierraLeone') {
      result.sierraLeone = filterByDay(preachingScheduleData.sierraLeone);
    }
    if (preachingCountryFilter === 'all' || preachingCountryFilter === 'guinea') {
      result.guinea = filterByDay(preachingScheduleData.guinea);
    }
    
    return result;
  };

  // Download Preaching Schedule as PDF - Beautiful Design with French/English Guinea pages
  const downloadPreachingSchedulePDF = () => {
    const doc = new jsPDF();
    const filteredData = getFilteredPreachingSchedule();
    
    // Header with gradient effect simulation
    doc.setFillColor(5, 150, 105);
    doc.rect(0, 0, 210, 45, 'F');
    
    // Logo area (circle)
    doc.setFillColor(255, 255, 255);
    doc.circle(105, 18, 8, 'F');
    doc.setFontSize(10);
    doc.setTextColor(5, 150, 105);
    doc.text('KIOO', 105, 19, { align: 'center' });
    doc.setFontSize(6);
    doc.text('98.1FM', 105, 23, { align: 'center' });
    
    // Title
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.text('Preaching Schedule', 105, 35, { align: 'center' });
    
    // Subtitle
    doc.setFontSize(10);
    doc.text('3-Nation Ministry Partners • Weekly Schedule', 105, 42, { align: 'center' });
    
    let yPosition = 55;
    
    // Filter info
    const countryLabel = preachingCountryFilter === 'all' ? 'All Countries' : 
      preachingCountryFilter === 'liberia' ? 'Liberia' : 
      preachingCountryFilter === 'sierraLeone' ? 'Sierra Leone' : 'Guinea';
    const dayLabel = preachingDayFilter === 'all' ? 'All Days' : 
      preachingDayFilter.charAt(0).toUpperCase() + preachingDayFilter.slice(1);
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`Filtered by: ${countryLabel} | ${dayLabel}`, 14, yPosition);
    yPosition += 10;
    
    // Liberia Section
    if (filteredData.liberia.length > 0) {
      doc.setFillColor(22, 163, 74);
      doc.rect(14, yPosition - 5, 182, 10, 'F');
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text('LIBERIA - Foya District Churches', 105, yPosition + 1, { align: 'center' });
      yPosition += 10;
      
      autoTable(doc, {
        startY: yPosition,
        head: [['Pastor / Church', 'Day(s)', 'Time (GMT)', 'Type']],
        body: filteredData.liberia.map(item => [item.pastor, item.days, item.time, item.type]),
        theme: 'grid',
        headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { halign: 'left' },
        columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 40, halign: 'center' }, 2: { cellWidth: 35, halign: 'center' }, 3: { cellWidth: 35, halign: 'center' } },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9, cellPadding: 3 }
      });
      yPosition = doc.lastAutoTable.finalY + 15;
    }
    
    // Sierra Leone Section
    if (filteredData.sierraLeone.length > 0) {
      if (yPosition > 220) { doc.addPage(); yPosition = 20; }
      
      doc.setFillColor(37, 99, 235);
      doc.rect(14, yPosition - 5, 182, 10, 'F');
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text('SIERRA LEONE - Partner Pastors', 105, yPosition + 1, { align: 'center' });
      yPosition += 10;
      
      autoTable(doc, {
        startY: yPosition,
        head: [['Pastor', 'Day', 'Time (GMT)', 'Type']],
        body: filteredData.sierraLeone.map(item => [item.pastor, item.days, item.time, item.type]),
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { halign: 'left' },
        columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 40, halign: 'center' }, 2: { cellWidth: 35, halign: 'center' }, 3: { cellWidth: 35, halign: 'center' } },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9, cellPadding: 3 }
      });
      yPosition = doc.lastAutoTable.finalY + 15;
    }
    
    // Guinea Section - ENGLISH VERSION
    if (filteredData.guinea.length > 0) {
      if (yPosition > 180) { doc.addPage(); yPosition = 20; }
      
      doc.setFillColor(234, 179, 8);
      doc.rect(14, yPosition - 5, 182, 10, 'F');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text('GUINEA - Partner Pastors (English Version)', 105, yPosition + 1, { align: 'center' });
      yPosition += 10;
      
      autoTable(doc, {
        startY: yPosition,
        head: [['Pastor', 'Day', 'Time (GMT)', 'Type']],
        body: filteredData.guinea.map(item => [item.pastor, item.days, item.time, item.type]),
        theme: 'grid',
        headStyles: { fillColor: [250, 204, 21], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
        bodyStyles: { halign: 'left' },
        columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 40, halign: 'center' }, 2: { cellWidth: 35, halign: 'center' }, 3: { cellWidth: 35, halign: 'center' } },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9, cellPadding: 3 }
      });
      
      // Guinea Section - FRENCH VERSION (New Page)
      doc.addPage();
      yPosition = 20;
      
      // French header
      doc.setFillColor(234, 179, 8);
      doc.rect(0, 0, 210, 35, 'F');
      doc.setFontSize(18);
      doc.setTextColor(0, 0, 0);
      doc.text('GUINÉE - Horaire de Prédication', 105, 15, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Pasteurs Partenaires • Version Française', 105, 25, { align: 'center' });
      
      yPosition = 45;
      
      // French day translations
      const dayTranslations = {
        'Monday': 'Lundi', 'Tuesday': 'Mardi', 'Wednesday': 'Mercredi',
        'Thursday': 'Jeudi', 'Friday': 'Vendredi', 'Saturday': 'Samedi', 'Sunday': 'Dimanche'
      };
      
      autoTable(doc, {
        startY: yPosition,
        head: [['Pasteur', 'Jour', 'Heure (GMT)', 'Type']],
        body: filteredData.guinea.map(item => [
          item.pastor, 
          dayTranslations[item.days] || item.days, 
          item.time, 
          item.type === 'Sermon' ? 'Prédication' : item.type
        ]),
        theme: 'grid',
        headStyles: { fillColor: [250, 204, 21], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
        bodyStyles: { halign: 'left' },
        columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 40, halign: 'center' }, 2: { cellWidth: 35, halign: 'center' }, 3: { cellWidth: 35, halign: 'center' } },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9, cellPadding: 3 }
      });
      
      yPosition = doc.lastAutoTable.finalY + 20;
      
      // French contact info
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Pour plus d\'informations, contactez Kioo Radio 98.1FM', 105, yPosition, { align: 'center' });
      doc.text('Foya, Lofa County, Liberia', 105, yPosition + 6, { align: 'center' });
    }
    
    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(5, 150, 105);
      doc.setLineWidth(0.5);
      doc.line(14, 282, 196, 282);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('Kioo Radio 98.1FM - The Gift of Good News', 14, 288);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 288, { align: 'center' });
      doc.text(`Page ${i} of ${pageCount}`, 196, 288, { align: 'right' });
    }
    
    doc.save('kioo-radio-preaching-schedule.pdf');
  };

  // Download Weekly Broadcast Schedule PDF
  const downloadWeeklySchedulePDF = () => {
    const doc = new jsPDF('landscape');
    
    // Header
    doc.setFillColor(5, 150, 105);
    doc.rect(0, 0, 297, 25, 'F');
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text('Kioo Radio 98.1FM - Weekly Broadcast Schedule', 148.5, 12, { align: 'center' });
    doc.setFontSize(10);
    doc.text('The Gift of Good News • Foya, Liberia', 148.5, 20, { align: 'center' });
    
    // Days of the week
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const daySchedules = {
      Monday: weekdaySchedule,
      Tuesday: weekdaySchedule,
      Wednesday: weekdaySchedule,
      Thursday: weekdaySchedule,
      Friday: weekdaySchedule,
      Saturday: saturdaySchedule,
      Sunday: sundaySchedule
    };
    
    // Create condensed schedule for each day
    const getCondensedSchedule = (schedule, dayName) => {
      return schedule.slice(0, 12).map(prog => {
        let programName = prog.Program;
        // Apply day override if exists
        const overrideKey = `${dayName.toLowerCase()}_override`;
        if (prog[overrideKey]) {
          programName = prog[overrideKey].Program;
        }
        return [prog["Start Time"], programName.substring(0, 25)];
      });
    };
    
    // Build table data - time slots as rows, days as columns
    const timeSlots = ['05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'];
    
    const findProgramAtTime = (schedule, time, dayName) => {
      const hour = parseInt(time.split(':')[0]);
      for (const prog of schedule) {
        const startHour = parseInt(prog["Start Time"].split(':')[0]);
        const endHour = parseInt(prog["End Time"].split(':')[0]);
        if (hour >= startHour && hour < endHour) {
          const overrideKey = `${dayName.toLowerCase()}_override`;
          if (prog[overrideKey]) {
            return prog[overrideKey].Program.substring(0, 18);
          }
          return prog.Program.substring(0, 18);
        }
      }
      return '-';
    };
    
    const tableBody = timeSlots.map(time => {
      const row = [time];
      days.forEach(day => {
        const schedule = daySchedules[day];
        row.push(findProgramAtTime(schedule, time, day));
      });
      return row;
    });
    
    autoTable(doc, {
      startY: 30,
      head: [['Time', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']],
      body: tableBody,
      theme: 'grid',
      headStyles: { fillColor: [5, 150, 105], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 8 },
      bodyStyles: { fontSize: 7, cellPadding: 2 },
      columnStyles: { 
        0: { cellWidth: 15, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 38 }, 2: { cellWidth: 38 }, 3: { cellWidth: 38 },
        4: { cellWidth: 38 }, 5: { cellWidth: 38 }, 6: { cellWidth: 38 }, 7: { cellWidth: 38 }
      },
      margin: { left: 10, right: 10 },
      alternateRowStyles: { fillColor: [245, 245, 245] }
    });
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleDateString()} | All times in GMT`, 148.5, 200, { align: 'center' });
    
    doc.save('kioo-radio-weekly-schedule.pdf');
  };

  // Download Daily Broadcast Schedule PDF
  const downloadDailySchedulePDF = (dayName = null) => {
    const day = dayName || selectedScheduleDay;
    const dayCapitalized = day.charAt(0).toUpperCase() + day.slice(1);
    
    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(5, 150, 105);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text('Kioo Radio 98.1FM', 105, 15, { align: 'center' });
    doc.setFontSize(16);
    doc.text(`${dayCapitalized} Broadcast Schedule`, 105, 28, { align: 'center' });
    doc.setFontSize(10);
    doc.text('The Gift of Good News • Foya, Liberia', 105, 36, { align: 'center' });
    
    // Get the right schedule
    let schedule;
    if (day === 'saturday') {
      schedule = saturdaySchedule;
    } else if (day === 'sunday') {
      schedule = sundaySchedule;
    } else {
      schedule = weekdaySchedule;
    }
    
    // Apply day overrides
    const processedSchedule = schedule.map(prog => {
      const overrideKey = `${day}_override`;
      if (prog[overrideKey]) {
        return {
          ...prog,
          Program: prog[overrideKey].Program,
          Language: prog[overrideKey].Language || prog.Language,
          Type: prog[overrideKey].Type || prog.Type
        };
      }
      return prog;
    });
    
    // Build table data
    const tableBody = processedSchedule.map(prog => [
      `${prog["Start Time"]} - ${prog["End Time"]}`,
      prog.Program,
      prog.Language,
      prog.Type
    ]);
    
    autoTable(doc, {
      startY: 50,
      head: [['Time (GMT)', 'Program', 'Language', 'Type']],
      body: tableBody,
      theme: 'striped',
      headStyles: { fillColor: [5, 150, 105], textColor: 255, fontStyle: 'bold', halign: 'center' },
      bodyStyles: { fontSize: 8, cellPadding: 2 },
      columnStyles: { 
        0: { cellWidth: 30, halign: 'center' },
        1: { cellWidth: 80 },
        2: { cellWidth: 30, halign: 'center' },
        3: { cellWidth: 35, halign: 'center' }
      },
      margin: { left: 14, right: 14 },
      didDrawPage: function(data) {
        // Footer on each page
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text('Kioo Radio 98.1FM - The Gift of Good News', 14, 287);
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 287, { align: 'center' });
        doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, 196, 287, { align: 'right' });
      }
    });
    
    doc.save(`kioo-radio-${day}-schedule.pdf`);
  };

  const downloadSchedule = () => {
    // Create PDF document
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('KIOO RADIO 98.1FM', 105, 15, { align: 'center' });
    
    doc.setFontSize(14);
    doc.text('PROGRAM SCHEDULE', 105, 23, { align: 'center' });
    
    // Add schedule title
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(getScheduleTitle(), 105, 31, { align: 'center' });
    
    // Prepare table data
    const tableData = filteredPrograms.map(program => [
      program['Start Time'],
      program['End Time'],
      translateProgramName(program.Program),
      program.Language,
      program.Type
    ]);
    
    // Add table
    autoTable(doc, {
      startY: 38,
      head: [[t('programs.startTime'), t('programs.endTime'), t('programs.program'), t('programs.language'), t('programs.type')]],
      body: tableData,
      theme: 'striped',
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      headStyles: {
        fillColor: [34, 139, 34],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 25, halign: 'center' },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 60 },
        3: { cellWidth: 35, halign: 'center' },
        4: { cellWidth: 35, halign: 'center' }
      },
      margin: { left: 10, right: 10 }
    });
    
    // Add footer with generation date
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont(undefined, 'italic');
      doc.text(
        `${t('programs.generatedOn')}: ${new Date().toLocaleString()}`,
        105,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
      doc.text(
        `${t('programs.page')} ${i} ${t('programs.of')} ${pageCount}`,
        105,
        doc.internal.pageSize.height - 5,
        { align: 'center' }
      );
    }
    
    // Save the PDF
    doc.save(`kioo-radio-schedule-${activeTab}.pdf`);
  };

  // Download schedule as ICS calendar file
  const downloadCalendar = () => {
    const dayMapping = {
      'Monday': 'MO',
      'Tuesday': 'TU', 
      'Wednesday': 'WE',
      'Thursday': 'TH',
      'Friday': 'FR',
      'Saturday': 'SA',
      'Sunday': 'SU'
    };

    // Get next occurrence of each weekday
    const getNextWeekday = (dayName) => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const today = new Date();
      const targetDay = days.indexOf(dayName);
      const todayDay = today.getDay();
      let diff = targetDay - todayDay;
      if (diff <= 0) diff += 7;
      const nextDate = new Date(today);
      nextDate.setDate(today.getDate() + diff);
      return nextDate;
    };

    // Format date for ICS (YYYYMMDD)
    const formatICSDate = (date, time) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const [hours, mins] = time.split(':');
      return `${year}${month}${day}T${hours.padStart(2, '0')}${mins.padStart(2, '0')}00`;
    };

    let icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Kioo Radio 98.1FM//Program Schedule//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
X-WR-CALNAME:Kioo Radio Programs
X-WR-TIMEZONE:GMT
`;

    // Get the appropriate schedule based on the current view
    const getScheduleForDay = (dayName) => {
      if (['Saturday'].includes(dayName)) return saturdaySchedule;
      if (['Sunday'].includes(dayName)) return sundaySchedule;
      return weekdaySchedule;
    };

    // Generate events for each day of the week
    const daysToProcess = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    daysToProcess.forEach(dayName => {
      const dayDate = getNextWeekday(dayName);
      const scheduleForDay = getScheduleForDay(dayName);
      
      // Use filtered programs if filters are applied, otherwise use the day's schedule
      const programsToUse = filteredPrograms.length > 0 ? filteredPrograms : scheduleForDay;

      programsToUse.forEach((program, index) => {
        const startTime = program['Start Time'];
        const endTime = program['End Time'];
        const programName = translateProgramName(program.Program);
        const uid = `kioo-${dayName.toLowerCase()}-${index}-${Date.now()}@kiooradio.org`;

        icsContent += `BEGIN:VEVENT
UID:${uid}
DTSTAMP:${formatICSDate(new Date(), '00:00')}Z
DTSTART:${formatICSDate(dayDate, startTime)}
DTEND:${formatICSDate(dayDate, endTime)}
RRULE:FREQ=WEEKLY;BYDAY=${dayMapping[dayName]}
SUMMARY:${programName}
DESCRIPTION:Language: ${program.Language}\\nType: ${program.Type}\\nKioo Radio 98.1FM
LOCATION:Kioo Radio 98.1FM
CATEGORIES:${program.Type}
STATUS:CONFIRMED
END:VEVENT
`;
      });
    });

    icsContent += 'END:VCALENDAR';

    // Download the ICS file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kioo-radio-schedule.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // === ADMIN PANEL COMPONENT ===
  const AdminPanel = () => {
    if (!isAdminAuthenticated) {
      return (
        <div className="fixed top-4 right-4 bg-white rounded-lg shadow-xl p-4 border border-gray-200 z-50">
          <h3 className="text-sm font-semibold mb-2">Admin Access</h3>
          <div className="flex gap-2">
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="Admin password"
              className="px-2 py-1 text-sm border rounded"
              onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
            />
            <button
              onClick={handleAdminLogin}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              Login
            </button>
          </div>
          <button
            onClick={() => setShowAdminPanel(false)}
            className="absolute top-1 right-1 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
      );
    }

    return (
      <div className="fixed top-4 right-4 bg-white rounded-lg shadow-xl p-6 border border-gray-200 z-50 max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Admin Panel</h3>
          <button
            onClick={() => setShowAdminPanel(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        
        {/* Status Display */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-sm">
            <div className={`font-semibold ${isPreviewMode ? 'text-orange-600' : 'text-green-600'}`}>
              Status: {isPreviewMode ? 'PREVIEW MODE' : 'LIVE MODE'}
            </div>
            <div className="text-gray-600">
              Backups: {backupHistory.length} | Changes: {changeLog.length}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Last backup: {backupHistory.length > 0 ? new Date(backupHistory[0].timestamp).toLocaleString() : 'None'}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <button
            onClick={togglePreviewMode}
            className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
              isPreviewMode 
                ? 'bg-orange-600 text-white hover:bg-orange-700' 
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {isPreviewMode ? '🔄 Exit Preview' : '👁️ Enter Preview'}
          </button>
          
          <button
            onClick={createBackupSnapshot}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            💾 Create Backup
          </button>
          
          <button
            onClick={() => setShowChangeLog(!showChangeLog)}
            className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
          >
            📋 {showChangeLog ? 'Hide' : 'Show'} Change Log
          </button>
        </div>

        {/* Quick Backup List */}
        {backupHistory.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Recent Backups</h4>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {backupHistory.slice(0, 3).map(backup => (
                <div key={backup.id} className="flex justify-between items-center text-xs">
                  <span className="text-gray-600">{backup.version}</span>
                  <button
                    onClick={() => restoreFromBackup(backup.id)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // === CHANGE LOG COMPONENT ===
  const ChangeLogPanel = () => {
    if (!showChangeLog) return null;

    return (
      <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-xl p-6 border border-gray-200 z-40 max-w-md max-h-96 overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Change Log</h3>
          <button
            onClick={() => setShowChangeLog(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        
        <div className="overflow-y-auto max-h-64 space-y-2">
          {changeLog.length === 0 ? (
            <p className="text-gray-500 text-sm">No changes recorded yet.</p>
          ) : (
            changeLog.map(entry => (
              <div key={entry.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start mb-1">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    entry.type === 'backup' ? 'bg-blue-100 text-blue-800' :
                    entry.type === 'restore' ? 'bg-green-100 text-green-800' :
                    entry.type === 'preview' ? 'bg-orange-100 text-orange-800' :
                    entry.type === 'admin' ? 'bg-purple-100 text-purple-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {entry.type.toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(entry.timestamp).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{entry.description}</p>
                {entry.details && (
                  <p className="text-xs text-gray-500 mt-1">{entry.details}</p>
                )}
              </div>
            ))
          )}
        </div>
        
        <div className="mt-4 pt-4 border-t">
          <button
            onClick={() => {
              setChangeLog([]);
              localStorage.removeItem('kioo-programs-changelog');
            }}
            className="text-xs text-red-600 hover:text-red-800"
          >
            Clear Change Log
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...PageSEO.programs} url="/programs" />
      
      {/* Admin Panel */}
      {showAdminPanel && <AdminPanel />}
      
      {/* Change Log Panel */}
      <ChangeLogPanel />

      {/* Preview Mode Banner */}
      {isPreviewMode && (
        <div className="fixed top-0 left-0 right-0 bg-orange-600 text-white py-2 px-4 z-30">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <span className="text-lg">⚠️</span>
              <span className="font-semibold">PREVIEW MODE ACTIVE</span>
              <span className="text-orange-200">- Changes are not live</span>
            </div>
            <button
              onClick={togglePreviewMode}
              className="bg-orange-700 hover:bg-orange-800 px-3 py-1 rounded text-sm font-medium"
            >
              Exit Preview
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <section className={`bg-kioo-primary text-white py-16 ${isPreviewMode ? 'mt-12' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              📅 {t('programs.title')}
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              {t('programs.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Color Legend */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-lg font-semibold text-kioo-dark mb-4 text-center">{t('programs.programTypeLegend')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.bibleTeaching')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.community')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-orange-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.interactive')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-purple-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.satellite')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.liveServices')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-yellow-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.music')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-indigo-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.devotional')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-pink-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.worship')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-teal-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.sermon')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-lime-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.youthPrograms')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-rose-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.outreach')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-cyan-500 rounded"></div>
              <span className="text-sm">{t('programs.legendCategories.educational')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Controls */}
      <section className="py-6 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            
            {/* Day Schedule Dropdown */}
            <div className="flex items-center gap-3">
              <label htmlFor="day-schedule-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                📅 Day Schedule:
              </label>
              <select
                id="day-schedule-filter"
                data-testid="day-schedule-dropdown"
                value={selectedScheduleDay}
                onChange={(e) => setSelectedScheduleDay(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent bg-white font-medium text-gray-700"
              >
                <option value="monday">{t('programs.monday')}</option>
                <option value="tuesday">{t('programs.tuesday')}</option>
                <option value="wednesday">{t('programs.wednesday')}</option>
                <option value="thursday">{t('programs.thursday')}</option>
                <option value="friday">{t('programs.friday')}</option>
                <option value="saturday">{t('programs.saturday')}</option>
                <option value="sunday">{t('programs.sunday')}</option>
              </select>
            </div>

            {/* Language and Type Filters */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              
              {/* Language Filter */}
              <div className="flex items-center gap-2">
                <label htmlFor="language-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t('programs.languageLabel')}
                </label>
                <select
                  id="language-filter"
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
                >
                  {languages.map(lang => {
                    let displayName;
                    if (lang === 'all') {
                      displayName = t('programs.allLanguages');
                    } else {
                      const langKey = lang.toLowerCase();
                      displayName = t(`clocks.languageNames.${langKey}`);
                    }
                    return (
                      <option key={lang} value={lang}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Program Type Filter */}
              <div className="flex items-center gap-2">
                <label htmlFor="type-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t('programs.typeLabel')}
                </label>
                <select
                  id="type-filter"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
                >
                  {programTypes.map(type => {
                    let displayName;
                    if (type === 'all') {
                      displayName = t('programs.allTypes');
                    } else {
                      // Map type to translation key
                      const typeKeyMap = {
                        'Bible Teaching': 'bibleTeaching',
                        'Community': 'community',
                        'Interactive': 'interactive',
                        'Satellite': 'satellite',
                        'Live Service': 'liveService',
                        'Music': 'music',
                        'Devotional': 'devotional',
                        'Worship': 'worship',
                        'Sermon': 'sermon',
                        'Youth/Community': 'youthCommunity',
                        'Outreach': 'outreach',
                        'Educational': 'educational'
                      };
                      const typeKey = typeKeyMap[type];
                      displayName = typeKey ? t(`clocks.programTypeNames.${typeKey}`) : type;
                    }
                    return (
                      <option key={type} value={type}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Weekly View Toggle */}
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={weeklyView}
                    onChange={(e) => setWeeklyView(e.target.checked)}
                    className="w-4 h-4 text-kioo-primary border-gray-300 rounded focus:ring-kioo-primary"
                  />
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    {t('programs.weeklyViewToggle')}
                  </span>
                </label>
              </div>

              {/* Download Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => downloadDailySchedulePDF()}
                  className="px-3 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors flex items-center gap-1 whitespace-nowrap text-sm"
                  title="Download daily schedule for selected day"
                >
                  📄 Daily PDF
                </button>
                <button
                  onClick={downloadWeeklySchedulePDF}
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1 whitespace-nowrap text-sm"
                  title="Download full weekly schedule"
                >
                  📋 Weekly PDF
                </button>
                <button
                  onClick={downloadCalendar}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 whitespace-nowrap text-sm"
                  title="Download as Calendar (ICS)"
                >
                  📅 Calendar
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Table */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Weekly View (Monday-Sunday) - Full 7 Days */}
          {weeklyView && activeTab === 'weekday' ? (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-kioo-primary text-white px-6 py-4">
                <h2 className="text-2xl font-bold">
                  📅 {t('programs.weeklyScheduleOverview')}
                </h2>
                <p className="text-green-100 mt-1">
                  {t('programs.complete7DaySchedule')}
                </p>
              </div>

              {/* Weekly Grid Layout - Now 8 columns (Time + 7 days) */}
              <div className="overflow-x-auto">
                <div className="grid grid-cols-8 gap-px bg-gray-200">
                  {/* Header Row */}
                  <div className="bg-gray-50 px-4 py-3 font-semibold text-sm text-gray-700 border-b">
                    {t('programs.timeGMT')}
                  </div>
                  <div className="bg-gray-50 px-4 py-3 font-semibold text-sm text-gray-700 border-b text-center">
                    {t('programs.monday')}
                  </div>
                  <div className="bg-gray-50 px-4 py-3 font-semibold text-sm text-gray-700 border-b text-center">
                    {t('programs.tuesday')}
                  </div>
                  <div className="bg-gray-50 px-4 py-3 font-semibold text-sm text-gray-700 border-b text-center">
                    {t('programs.wednesday')}
                  </div>
                  <div className="bg-gray-50 px-4 py-3 font-semibold text-sm text-gray-700 border-b text-center">
                    {t('programs.thursday')}
                  </div>
                  <div className="bg-gray-50 px-4 py-3 font-semibold text-sm text-gray-700 border-b text-center">
                    {t('programs.friday')}
                  </div>
                  <div className="bg-blue-50 px-4 py-3 font-semibold text-sm text-blue-700 border-b text-center">
                    {t('programs.saturday')}
                  </div>
                  <div className="bg-purple-50 px-4 py-3 font-semibold text-sm text-purple-700 border-b text-center">
                    {t('programs.sunday')}
                  </div>

                  {/* Program Rows */}
                  {filteredPrograms.map((program, index) => {
                    const isHighlight = program.highlight;
                    const isFrench = program.Language === 'French' || program.Language === 'fr';
                    const isSpecial = program.monday_override || program.tuesday_override || program.friday_override || program.thursday_override;
                    
                    return (
                      <React.Fragment key={index}>
                        {/* Time Column */}
                        <div className={`bg-white px-4 py-3 text-sm font-medium border-b ${isHighlight ? 'bg-yellow-50' : ''}`}>
                          {program['Start Time']} - {program['End Time']}
                        </div>
                        
                        {/* Monday */}
                        <div className={`bg-white px-2 py-3 text-xs border-b ${isHighlight ? 'bg-yellow-50' : ''}`}>
                          <div className="font-medium">
                            {translateProgramName(program.monday_override ? program.monday_override.Program : program.Program)}
                          </div>
                          <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs mt-1 ${
                            isFrench || (program.monday_override && program.monday_override.Language === 'French') ? 'bg-purple-100 text-purple-800' : 
                            program.Language === 'English' ? 'bg-blue-100 text-blue-800' :
                            program.Language === 'Kissi' ? 'bg-green-100 text-green-800' :
                            program.Language === 'Gbandi' || (program.monday_override && program.monday_override.Language === 'Gbandi') ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {program.monday_override && program.monday_override.Language ? program.monday_override.Language : program.Language}
                          </div>
                          {program.monday_override && program.monday_override.Language === 'Gbandi' && (
                            <div className="text-xs text-orange-600 font-medium mt-1">⭐ Gbandi Special</div>
                          )}
                          {program.monday_override && program.monday_override.Language === 'French' && (
                            <div className="text-xs text-purple-600 font-medium mt-1">⭐ French Special</div>
                          )}
                        </div>
                        
                        {/* Tuesday */}
                        <div className={`bg-white px-2 py-3 text-xs border-b ${isHighlight ? 'bg-yellow-50' : ''}`}>
                          <div className="font-medium">
                            {translateProgramName(program.tuesday_override ? program.tuesday_override.Program : program.Program)}
                          </div>
                          <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs mt-1 ${
                            isFrench ? 'bg-purple-100 text-purple-800' : 
                            program.Language === 'English' ? 'bg-blue-100 text-blue-800' :
                            program.Language === 'Kissi' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {program.Language}
                          </div>
                          {program.tuesday_override && (
                            <div className="text-xs text-purple-600 font-medium mt-1">⭐ French Special</div>
                          )}
                        </div>
                        
                        {/* Wednesday */}
                        <div className={`bg-white px-2 py-3 text-xs border-b ${isHighlight ? 'bg-yellow-50' : ''}`}>
                          <div className="font-medium">{translateProgramName(program.Program)}</div>
                          <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs mt-1 ${
                            isFrench ? 'bg-purple-100 text-purple-800' : 
                            program.Language === 'English' ? 'bg-blue-100 text-blue-800' :
                            program.Language === 'Kissi' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {program.Language}
                          </div>
                        </div>
                        
                        {/* Thursday */}
                        <div className={`bg-white px-2 py-3 text-xs border-b ${isHighlight ? 'bg-yellow-50' : ''}`}>
                          <div className="font-medium">
                            {translateProgramName(program.thursday_override ? program.thursday_override.Program : program.Program)}
                          </div>
                          <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs mt-1 ${
                            isFrench || (program.thursday_override && program.thursday_override.Language === 'French') ? 'bg-purple-100 text-purple-800' : 
                            program.Language === 'English' ? 'bg-blue-100 text-blue-800' :
                            program.Language === 'Kissi' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {program.thursday_override && program.thursday_override.Language ? program.thursday_override.Language : program.Language}
                          </div>
                          {program.thursday_override && (
                            <div className="text-xs text-purple-600 font-medium mt-1">⭐ French Special</div>
                          )}
                        </div>
                        
                        {/* Friday */}
                        <div className={`bg-white px-2 py-3 text-xs border-b ${isHighlight ? 'bg-yellow-50' : ''}`}>
                          <div className="font-medium">
                            {translateProgramName(program.friday_override ? program.friday_override.Program : program.Program)}
                          </div>
                          <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs mt-1 ${
                            isFrench || (program.friday_override && program.friday_override.Language === 'French') ? 'bg-purple-100 text-purple-800' : 
                            program.Language === 'English' ? 'bg-blue-100 text-blue-800' :
                            program.Language === 'Kissi' ? 'bg-green-100 text-green-800' :
                            program.Language === 'Mixed' || (program.friday_override && program.friday_override.Language === 'Mixed') ? 'bg-indigo-100 text-indigo-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {program.friday_override && program.friday_override.Language ? program.friday_override.Language : program.Language}
                          </div>
                          {program.friday_override && program.friday_override.Language === 'French' && (
                            <div className="text-xs text-purple-600 font-medium mt-1">⭐ French Special</div>
                          )}
                          {program.friday_override && program.friday_override.Language === 'Mixed' && (
                            <div className="text-xs text-indigo-600 font-medium mt-1">⭐ Weekly Special</div>
                          )}
                        </div>
                        
                        {/* Saturday */}
                        <div className={`bg-blue-50 px-2 py-3 text-xs border-b ${isHighlight ? 'bg-yellow-50' : ''}`}>
                          {(() => {
                            const satProgram = saturdaySchedule.find(p => p['Start Time'] === program['Start Time']);
                            if (satProgram) {
                              return (
                                <>
                                  <div className="font-medium">{translateProgramName(satProgram.Program)}</div>
                                  <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs mt-1 ${
                                    satProgram.Language === 'French' ? 'bg-purple-100 text-purple-800' : 
                                    satProgram.Language === 'English' ? 'bg-blue-100 text-blue-800' :
                                    satProgram.Language === 'Kissi' ? 'bg-green-100 text-green-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {satProgram.Language}
                                  </div>
                                </>
                              );
                            }
                            return <div className="text-gray-400 italic">—</div>;
                          })()}
                        </div>
                        
                        {/* Sunday */}
                        <div className={`bg-purple-50 px-2 py-3 text-xs border-b ${isHighlight ? 'bg-yellow-50' : ''}`}>
                          {(() => {
                            const sunProgram = sundaySchedule.find(p => p['Start Time'] === program['Start Time']);
                            if (sunProgram) {
                              return (
                                <>
                                  <div className="font-medium">{translateProgramName(sunProgram.Program)}</div>
                                  <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs mt-1 ${
                                    sunProgram.Language === 'French' ? 'bg-purple-100 text-purple-800' : 
                                    sunProgram.Language === 'English' ? 'bg-blue-100 text-blue-800' :
                                    sunProgram.Language === 'Kissi' ? 'bg-green-100 text-green-800' :
                                    sunProgram.Language === 'Mixed' ? 'bg-indigo-100 text-indigo-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {sunProgram.Language}
                                  </div>
                                </>
                              );
                            }
                            return <div className="text-gray-400 italic">—</div>;
                          })()}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Weekly Highlights Section */}
              <div className="bg-gray-50 px-6 py-4 border-t">
                <h3 className="font-semibold text-gray-800 mb-3">📌 {t('programs.weeklyHighlights')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="font-medium text-purple-800">French Programming</div>
                    <div className="text-sm text-purple-600 mt-1">
                      • Tuesday 17:00: Malaika<br/>
                      • Friday 09:45: Mission Afrique<br/>
                      • Friday 16:00: Renaissance<br/>
                      • Daily: French devotionals
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="font-medium text-blue-800">English Programming</div>
                    <div className="text-sm text-blue-600 mt-1">
                      • Daily Bible Teaching<br/>
                      • Morning Devotionals<br/>
                      • Pastor's Corner Series<br/>
                      • Community Programming
                    </div>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                    <div className="font-medium text-indigo-800">Weekly Specials</div>
                    <div className="text-sm text-indigo-600 mt-1">
                      • <strong>Friday 18:00: Women's Empowerment Hour</strong><br/>
                      • Community Focus<br/>
                      • Mixed Language Content<br/>
                      • Special Programs
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="font-medium text-green-800">Local Languages</div>
                    <div className="text-sm text-green-600 mt-1">
                      • Kissi Programming<br/>
                      • <strong>Monday 16:30: Mandingo Programming (1 hour)</strong><br/>
                      • <strong>Tuesday 16:30: Mandingo Bible Teaching</strong><br/>
                      • <strong>Monday 19:00: Gbandi Language Hour</strong><br/>
                      • Community Focus<br/>
                      • Cultural Content
                    </div>
                  </div>
                </div>
              </div>

              {/* Staff Schedule Section */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 px-6 py-6 border-t-4 border-kioo-primary">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-800 flex items-center">
                    👥 {t('programs.countryStaffSchedule')}
                  </h3>
                  <span className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full">
                    {t('programs.toBeUpdated')}
                  </span>
                </div>
                
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('programs.staffName')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('programs.role')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('programs.workingDays')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('programs.workingHours')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Placeholder Staff Entries */}
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-400 italic">
                          [Staff Name 1]
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 italic">
                          [Role - e.g., Station Manager]
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          <div className="flex gap-1">
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs italic">Mon</span>
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs italic">Tue</span>
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs italic">Wed</span>
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs italic">Thu</span>
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs italic">Fri</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 italic">
                          [8:00 AM - 5:00 PM]
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-400 italic">
                          [Staff Name 2]
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 italic">
                          [Role - e.g., Technical Director]
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          <div className="flex gap-1">
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs italic">Mon</span>
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs italic">Wed</span>
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs italic">Fri</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 italic">
                          [9:00 AM - 6:00 PM]
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-400 italic">
                          [Staff Name 3]
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 italic">
                          [Role - e.g., Program Presenter]
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          <div className="flex gap-1">
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs italic">Tue</span>
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs italic">Thu</span>
                            <span className="px-2 py-1 bg-blue-100 rounded text-xs italic">Sat</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 italic">
                          [6:00 AM - 2:00 PM]
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  
                  {/* Instructions for updating */}
                  <div className="bg-yellow-50 border-t border-yellow-200 px-6 py-3">
                    <div className="flex items-start">
                      <span className="text-yellow-600 mr-2">ℹ️</span>
                      <p className="text-sm text-yellow-700">
                        {t('programs.noteStaffSchedule')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Regular Schedule Table */
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-kioo-primary text-white px-6 py-4">
                <h2 className="text-2xl font-bold">
                  {getScheduleTitle()}
                </h2>
                <p className="text-green-100 mt-1">
                  {filteredPrograms.length} programs
                  {selectedLanguage !== 'all' && ` in ${selectedLanguage}`}
                  {selectedType !== 'all' && ` (${selectedType})`}
                  {(selectedLanguage !== 'all' || selectedType !== 'all') && 
                    ` • ${getCurrentSchedule().length} total`}
                </p>
              </div>

              {/* Responsive Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Program
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Language
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPrograms.map((program, index) => (
                      <tr 
                        key={index} 
                        className={`hover:bg-gray-50 ${program.highlight ? 'bg-yellow-50 border-l-4 border-yellow-400' : ''}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {program['Start Time']} - {program['End Time']}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="font-medium">{translateProgramName(program.Program)}</div>
                          {program.highlight && (
                            <div className="text-xs text-yellow-600 font-medium">⭐ Special Program</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {program.Language}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            typeColors[program.Type] || 'bg-gray-100 text-gray-800'
                          }`}>
                            {program.Type}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Weekly Special Programs */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-2xl font-bold text-kioo-dark mb-6 text-center">
              🌟 {t('programs.weeklySpecialSection')}
            </h3>
            <p className="text-center text-gray-600 mb-6">
              {t('programs.weeklySpecialDesc')}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {weeklySpecial.map((special, index) => {
                // Translate badge text
                const translateBadge = (badge) => {
                  if (!badge) return '';
                  if (badge.includes('Weekly Special')) return t('programs.badges.weeklySpecial');
                  if (badge.includes('French Youth Special')) return t('programs.badges.frenchYouthSpecial');
                  if (badge.includes('French Special')) return t('programs.badges.frenchSpecial');
                  if (badge.includes('Bible Special')) return t('programs.badges.bibleSpecial');
                  if (badge.includes('Local Special')) return t('programs.badges.localSpecial');
                  return badge;
                };

                // Translate day name
                const translateDay = (day) => {
                  const dayMap = {
                    'Monday': t('programs.monday'),
                    'Tuesday': t('programs.tuesday'),
                    'Wednesday': t('programs.wednesday'),
                    'Thursday': t('programs.thursday'),
                    'Friday': t('programs.friday'),
                    'Saturday': t('programs.saturday'),
                    'Sunday': t('programs.sunday')
                  };
                  return dayMap[day] || day;
                };

                return (
                  <div key={index} className="bg-gradient-to-r from-kioo-primary to-kioo-secondary rounded-lg p-6 text-white">
                    <div className="flex justify-between items-start mb-3">
                      <div className="text-2xl">
                        {special.Program === "Gbandi Language Hour" ? "🗣️" :
                         special.Program === "Youth Connect Special" ? "👥" :
                         special.Program === "Women's Empowerment Hour" ? "👩‍💼" :
                         special.Program === "Makona Talk Show" ? "🎙️" : "📅"}
                      </div>
                      {special.badge && (
                        <span className="text-xs bg-white/20 px-2 py-1 rounded-full text-white">
                          {translateBadge(special.badge)}
                        </span>
                      )}
                    </div>
                    <h4 className="text-lg font-semibold mb-2">{translateProgramName(special.Program)}</h4>
                    <p className="text-green-100 text-sm mb-1">{translateDay(special.Day)} • {special.Time}</p>
                    <p className="text-green-100 text-sm mb-2">{special.Language} • {special.Type}</p>
                    {special.description && (
                      <p className="text-green-50 text-xs italic border-t border-green-300 pt-2">
                        {special.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Language Statistics */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-2xl font-bold text-kioo-dark mb-6 text-center">
              🗣️ {t('programs.languageDistribution')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">41.7%</div>
                <div className="text-sm text-gray-600">{t('languages.kissi')}</div>
                <div className="text-xs text-gray-500">10 hours daily</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">25.0%</div>
                <div className="text-sm text-gray-600">{t('languages.english')}</div>
                <div className="text-xs text-gray-500">6 hours daily</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">16.7%</div>
                <div className="text-sm text-gray-600">{t('languages.french')}</div>
                <div className="text-xs text-gray-500">4 hours daily</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">8.3%</div>
                <div className="text-sm text-gray-600">{t('languages.mixed')}</div>
                <div className="text-xs text-gray-500">2 hours daily</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">4.2%</div>
                <div className="text-sm text-gray-600">{t('languages.mandingo')}</div>
                <div className="text-xs text-gray-500">1 hour daily</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">4.2%</div>
                <div className="text-sm text-gray-600">{t('languages.fula')}</div>
                <div className="text-xs text-gray-500">1 hour daily</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Preaching Schedule by Country */}
      <section className="py-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-kioo-dark mb-2">
                🎤 Preaching Schedule by Country
              </h2>
              <p className="text-gray-600 text-sm">View and download the weekly preaching times from our 3-nation ministry partners</p>
            </div>
            
            {/* Filter Controls */}
            <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Country:</label>
                <select
                  value={preachingCountryFilter}
                  onChange={(e) => setPreachingCountryFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent bg-white text-sm"
                  data-testid="preaching-country-filter"
                >
                  <option value="all">All Countries</option>
                  <option value="liberia">🇱🇷 Liberia</option>
                  <option value="sierraLeone">🇸🇱 Sierra Leone</option>
                  <option value="guinea">🇬🇳 Guinea</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Day:</label>
                <select
                  value={preachingDayFilter}
                  onChange={(e) => setPreachingDayFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent bg-white text-sm"
                  data-testid="preaching-day-filter"
                >
                  <option value="all">All Days</option>
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday</option>
                  <option value="sunday">Sunday</option>
                </select>
              </div>
              <button
                onClick={downloadPreachingSchedulePDF}
                className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md font-medium"
                data-testid="download-preaching-pdf"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download PDF
              </button>
            </div>

            {/* Filtered Schedule Display */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-kioo-primary text-white">
                    <th className="px-4 py-3 text-left text-sm font-semibold">Country</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Pastor / Church</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Day(s)</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Time (GMT)</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredPreachingSchedule().liberia.map((item, index) => (
                    <tr key={`lib-${index}`} className="border-b hover:bg-green-50">
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          🇱🇷 Liberia
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.pastor}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{item.days}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{item.time}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        <span className={`px-2 py-1 rounded text-xs ${item.type === 'Sermon' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                          {item.type}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {getFilteredPreachingSchedule().sierraLeone.map((item, index) => (
                    <tr key={`sl-${index}`} className="border-b hover:bg-blue-50">
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          🇸🇱 Sierra Leone
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.pastor}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{item.days}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{item.time}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">{item.type}</span>
                      </td>
                    </tr>
                  ))}
                  {getFilteredPreachingSchedule().guinea.map((item, index) => (
                    <tr key={`gn-${index}`} className="border-b hover:bg-yellow-50">
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                          🇬🇳 Guinea
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.pastor}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{item.days}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{item.time}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">{item.type}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {getFilteredPreachingSchedule().liberia.length === 0 && 
               getFilteredPreachingSchedule().sierraLeone.length === 0 && 
               getFilteredPreachingSchedule().guinea.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No preaching programs found for the selected filters.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Live Broadcast Days Section */}
      {liveBroadcastSchedule && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-kioo-dark mb-4">
                📡 {t('programs.liveBroadcastDays')}
              </h2>
              <div className="max-w-4xl mx-auto">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t('programs.liveBroadcastDesc')}
                </p>
              </div>
            </div>

            {/* Live Broadcast Type Legend */}
            <div className="mb-8 bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-kioo-dark mb-4 text-center">{t('programs.broadcastStatusLegend')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span className="text-sm">{t('programs.liveBroadcasting')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-gray-400 rounded"></div>
                  <span className="text-sm">{t('programs.preRecorded')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-purple-500 rounded"></div>
                  <span className="text-sm">{t('programs.sundayRotation')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                  <span className="text-sm">{t('programs.countrySpecific')}</span>
                </div>
              </div>
            </div>

            {/* Filter Controls */}
            <div className="mb-8 bg-white border rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-kioo-dark mb-4">{t('programs.filterOptions')}</h3>
              <div className="grid md:grid-cols-3 gap-4">
                
                {/* Day Filter */}
                <div>
                  <label htmlFor="day-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('programs.filterByDay')}
                  </label>
                  <select
                    id="day-filter"
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
                  >
                    <option value="all">{t('programs.allDays')}</option>
                    {days.slice(1).map(day => {
                      const dayMap = {
                        'monday': t('programs.monday'),
                        'tuesday': t('programs.tuesday'),
                        'wednesday': t('programs.wednesday'),
                        'thursday': t('programs.thursday'),
                        'friday': t('programs.friday'),
                        'saturday': t('programs.saturday'),
                        'sunday': t('programs.sunday')
                      };
                      return (
                        <option key={day} value={day}>
                          {dayMap[day] || day}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Country Filter */}
                <div>
                  <label htmlFor="country-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('programs.filterByCountry')}
                  </label>
                  <select
                    id="country-filter"
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
                  >
                    <option value="all">{t('programs.allCountries')}</option>
                    <option value="liberia">🇱🇷 {t('countries.liberia')}</option>
                    <option value="sierra_leone">🇸🇱 {t('countries.sierraLeone')}</option>
                    <option value="guinea">🇬🇳 {t('countries.guinea')}</option>
                  </select>
                </div>

                {/* Broadcast Type Filter */}
                <div>
                  <label htmlFor="broadcast-type-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('programs.filterByType')}
                  </label>
                  <select
                    id="broadcast-type-filter"
                    value={selectedBroadcastType}
                    onChange={(e) => setSelectedBroadcastType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary focus:border-transparent"
                  >
                    <option value="all">{t('programs.allTypes')}</option>
                    <option value="live">{t('programs.liveOnly')}</option>
                    <option value="pre-recorded">{t('programs.preRecordedOnly')}</option>
                    <option value="rotation">{t('programs.sundayRotation')}</option>
                  </select>
                </div>
              </div>

              {/* Active Filters Display */}
              {(selectedDay !== 'all' || selectedCountry !== 'all' || selectedBroadcastType !== 'all') && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600">Active filters:</span>
                  {selectedDay !== 'all' && (
                    <span className="bg-kioo-primary text-white px-2 py-1 rounded text-xs">
                      Day: {selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}
                    </span>
                  )}
                  {selectedCountry !== 'all' && (
                    <span className="bg-kioo-primary text-white px-2 py-1 rounded text-xs">
                      Country: {selectedCountry === 'sierra_leone' ? 'Sierra Leone' : 
                               selectedCountry === 'liberia' ? 'Liberia' : 'Guinea'}
                    </span>
                  )}
                  {selectedBroadcastType !== 'all' && (
                    <span className="bg-kioo-primary text-white px-2 py-1 rounded text-xs">
                      Type: {selectedBroadcastType}
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setSelectedDay('all');
                      setSelectedCountry('all');
                      setSelectedBroadcastType('all');
                    }}
                    className="text-xs text-red-600 hover:text-red-800 underline"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>

            {/* Weekly Schedule Grid */}
            {(() => {
              const filteredSchedule = getFilteredBroadcastSchedule();
              const scheduleEntries = Object.entries(filteredSchedule.weeklySchedule);
              
              // Helper function to translate day names
              const translateDayName = (day) => {
                const dayMap = {
                  'monday': t('programs.monday'),
                  'tuesday': t('programs.tuesday'),
                  'wednesday': t('programs.wednesday'),
                  'thursday': t('programs.thursday'),
                  'friday': t('programs.friday'),
                  'saturday': t('programs.saturday'),
                  'sunday': t('programs.sunday')
                };
                return dayMap[day] || day;
              };

              return (
                <div className="mb-12">
                  <h3 className="text-2xl font-semibold text-kioo-dark mb-6 text-center">
                    {t('programs.weeklyScheduleTitle')}
                    {selectedDay !== 'all' && ` - ${translateDayName(selectedDay)}`}
                  </h3>
                  <div className="overflow-x-auto">
                    <div className={`grid gap-2 min-w-full ${scheduleEntries.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' : 'grid-cols-7'}`}>
                      {scheduleEntries.map(([day, countries]) => (
                        <div key={day} className="text-center">
                          <div className="font-semibold text-gray-800 mb-2 capitalize text-sm lg:text-base">
                            {translateDayName(day)}
                          </div>
                          <div className="space-y-1">
                            {/* Liberia */}
                            {(selectedCountry === 'all' || selectedCountry === 'liberia') && 
                             (matchesBroadcastTypeFilter(countries.liberia)) && (
                              <div className={`p-2 rounded text-xs lg:text-sm font-medium ${
                                countries.liberia === 'live' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                🇱🇷 {countries.liberia === 'live' ? t('programs.live') : t('programs.preRec')}
                              </div>
                            )}
                            {/* Sierra Leone */}
                            {(selectedCountry === 'all' || selectedCountry === 'sierra_leone') && 
                             (matchesBroadcastTypeFilter(countries.sierra_leone)) && (
                              <div className={`p-2 rounded text-xs lg:text-sm font-medium ${
                                countries.sierra_leone === 'live' ? 'bg-blue-100 text-blue-800' : 
                                countries.sierra_leone === 'rotation' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                🇸🇱 {countries.sierra_leone === 'live' ? t('programs.live') : countries.sierra_leone === 'rotation' ? t('programs.rotation') : t('programs.preRec')}
                              </div>
                            )}
                            {/* Guinea */}
                            {(selectedCountry === 'all' || selectedCountry === 'guinea') && 
                             (matchesBroadcastTypeFilter(countries.guinea)) && (
                              <div className={`p-2 rounded text-xs lg:text-sm font-medium ${
                                countries.guinea === 'live' ? 'bg-yellow-100 text-yellow-800' : 
                                countries.guinea === 'rotation' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                🇬🇳 {countries.guinea === 'live' ? t('programs.live') : countries.guinea === 'rotation' ? t('programs.rotation') : t('programs.preRec')}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Country Summary Table */}
            {(() => {
              const filteredCountries = getFilteredCountrySchedules();
              
              // Helper to translate country names
              const translateCountryName = (country) => {
                if (country === 'Liberia') return t('countries.liberia');
                if (country === 'Sierra Leone') return t('countries.sierraLeone');
                if (country === 'Guinea') return t('countries.guinea');
                return country;
              };

              // Helper to translate day arrays
              const translateDayArray = (days) => {
                const dayMap = {
                  'Monday': t('programs.monday'),
                  'Tuesday': t('programs.tuesday'),
                  'Wednesday': t('programs.wednesday'),
                  'Thursday': t('programs.thursday'),
                  'Friday': t('programs.friday'),
                  'Saturday': t('programs.saturday'),
                  'Sunday': t('programs.sunday')
                };
                return days.map(day => dayMap[day] || day).join(', ');
              };

              return filteredCountries.length > 0 && (
                <div className="mb-12">
                  <h3 className="text-2xl font-semibold text-kioo-dark mb-6 text-center">
                    {t('programs.countrySummary')}
                    {selectedCountry !== 'all' && ` - ${selectedCountry === 'sierra_leone' ? t('countries.sierraLeone') : 
                                                       selectedCountry === 'liberia' ? t('countries.liberia') : t('countries.guinea')}`}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg shadow-md overflow-hidden">
                      <thead className="bg-kioo-primary text-white">
                        <tr>
                          <th className="px-6 py-4 text-left font-semibold">{t('programs.country')}</th>
                          <th className="px-6 py-4 text-left font-semibold">{t('programs.liveDays')}</th>
                          <th className="px-6 py-4 text-left font-semibold">{t('programs.preRecordedDays')}</th>
                          <th className="px-6 py-4 text-left font-semibold">{t('programs.specialNote')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCountries.map((country, index) => (
                          <tr key={country.country} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className={`w-4 h-4 rounded-full mr-3 ${
                                  country.colorCode === 'green' ? 'bg-green-500' :
                                  country.colorCode === 'blue' ? 'bg-blue-500' : 'bg-yellow-500'
                                }`}></div>
                                <span className="font-medium text-gray-900">
                                  {country.country === 'Liberia' ? '🇱🇷' : country.country === 'Sierra Leone' ? '🇸🇱' : '🇬🇳'} {translateCountryName(country.country)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-gray-700">
                              {country.liveDays.length === 7 ? t('programs.dailyMonSun') : translateDayArray(country.liveDays)}
                            </td>
                            <td className="px-6 py-4 text-gray-700">
                              {country.preRecordedDays.length === 0 ? '—' : translateDayArray(country.preRecordedDays)}
                            </td>
                            <td className="px-6 py-4 text-gray-600 text-sm">
                              {country.specialNote}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* Visual Country Cards */}
            {(() => {
              const filteredCountries = getFilteredCountrySchedules();
              
              return (
                <div className={`grid gap-6 ${
                  filteredCountries.length === 1 ? 'md:grid-cols-1 max-w-sm mx-auto' :
                  filteredCountries.length === 2 ? 'md:grid-cols-2' :
                  'md:grid-cols-2 lg:grid-cols-4'
                }`}>
                  {/* Liberia Card */}
                  {(selectedCountry === 'all' || selectedCountry === 'liberia') && (
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                      <div className="text-center">
                        <div className="text-4xl mb-3">🇱🇷</div>
                        <h4 className="text-lg font-semibold text-green-800 mb-2">{t('countries.liberia')}</h4>
                        <div className="text-green-700 font-medium mb-2">{t('programs.liveEveryDay')}</div>
                        <div className="text-sm text-green-600">{t('programs.monday').substring(0,3)}–{t('programs.sunday').substring(0,3)}</div>
                      </div>
                    </div>
                  )}

                  {/* Sierra Leone Card */}
                  {(selectedCountry === 'all' || selectedCountry === 'sierra_leone') && (
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                      <div className="text-center">
                        <div className="text-4xl mb-3">🇸🇱</div>
                        <h4 className="text-lg font-semibold text-blue-800 mb-2">{t('countries.sierraLeone')}</h4>
                        <div className="text-blue-700 font-medium mb-2">{t('programs.liveOnTueFri')}</div>
                        <div className="text-sm text-blue-600">{t('programs.tuesday')}, {t('programs.friday')}</div>
                      </div>
                    </div>
                  )}

                  {/* Guinea Card */}
                  {(selectedCountry === 'all' || selectedCountry === 'guinea') && (
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border border-yellow-200">
                      <div className="text-center">
                        <div className="text-4xl mb-3">🇬🇳</div>
                        <h4 className="text-lg font-semibold text-yellow-800 mb-2">{t('countries.guinea')}</h4>
                        <div className="text-yellow-700 font-medium mb-2">{t('programs.liveOnWedSat')}</div>
                        <div className="text-sm text-yellow-600">{t('programs.wednesday')}, {t('programs.saturday')}</div>
                      </div>
                    </div>
                  )}

                  {/* Sunday Service Card */}
                  {selectedDay === 'all' || selectedDay === 'sunday' ? (
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                      <div className="text-center">
                        <div className="text-4xl mb-3">⛪</div>
                        <h4 className="text-lg font-semibold text-purple-800 mb-2">{t('programs.sundayLiveService')}</h4>
                        <div className="text-purple-700 font-medium mb-2">{t('programs.rotatesWeekly')}</div>
                        <div className="text-sm text-purple-600">{t('programs.allThreeCountries')}</div>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })()}

            {/* Color Legend */}
            <div className="mt-8 bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 text-center">{t('programs.colorLegend')}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                  <span>{t('programs.liberiaDailyLive')}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
                  <span>{t('programs.sierraLeoneTueFri')}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-yellow-500 rounded-full mr-2"></div>
                  <span>{t('programs.guineaWedSat')}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-gray-400 rounded-full mr-2"></div>
                  <span>{t('programs.preRecorded')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Programs;